/* eslint-disable no-unused-vars */
import store from '@/store';
import { log as debugLog, error as debugError } from '@/utils/debugLogger';
const THREE = window.THREE;
import { ForgeInspection, ForgeEquipment, ForgeSettings, ForgeTagShortInfo, ForgeStructural, ForgePipeline, ForgeScaffold, ForgeAnnotation, ForgeBaseTag, ForgeAnomaly, ForgeAvaInspect, CmlRecord } from './../../scripts/js/forgeModels';
import { missingMasterCatalogEntry } from '@/features/Tags/utils/masterCatalogPlacement';
import { forgeFetch, forgeFetchFacility, postDataAsync } from './../../utils/mongoUtils';
import HttpService from './../../utils/httpService';
import { FACILITY_REQUEST } from './../../utils/http/RequestRegistry';
import { objectToQueryParams } from '@/scripts/js/forgeService';
import Vue from 'vue';
import router from '@/router';
import { getAllParentsIds } from '@/utils/forgeUtils';
import tagService from '@/features/Tags/services/baseTag.service';
import registerService from '@/features/Registers/services/registerService';
import axios from 'axios';
import { GET_ALL_DOCUMENTS } from '@/utils/attachmentsAPI';
import { API_LIMITS } from '@/constants/apiLimits';
import appConfig from '../../../appConfig';
import { getDocumentBlobUrl, getDocumentId } from '@/utils/documentProxyUtils';
import { matchesBucket } from '@/utils/colorBuckets';
import { CORROSION_RATE_BANDS, REMAINING_LIFE_BANDS, THICKNESS_BANDS } from '@/features/Dashboard/utils/inspectionBandDefaults';
import { RegisterType } from '@/features/Registers/config/enums';
import {
    REGISTER_FILTER_EMPTY_TAG_ID,
    REGISTER_VIEWER_ACTION,
    normalizeRegisterType,
} from '@/features/Registers/config/registerViewerContext';
import {
    TAG_SPATIAL_INTENTS,
    resolveTagSpatialIntent,
} from '@/features/Tags/utils/tagSpatialNavigation';
import { executeTagSpatialIntent } from '@/features/Tags/utils/tagSpatialNavigationExecutor';
import { applyTagPlacementFields } from '@/features/Tags/utils/tagPlacementFields';
import { normalizeTagInfoTabRequest, tagInfoTabRequest } from '@/features/Tags/utils/tagInfoTabRequest';
import { linkedRecordsCacheKey } from '@/features/Tags/components/records/linkedRecordsContext';
import permissions from '@Share/config/permissions';
import { $has } from '@/utils';
import {
    TAG_MARKER_OUTCOME,
    createCatalogReconciliationReceipts,
    createTagMarkerLifecycleReceipt,
    resolveTagMarkerPoint,
    upsertTagById,
} from '@/features/Tags/utils/tagMarkerLifecycle';

/** Lifecycle of one tag's linked-register-records entry in `linkedRecordsByTagId`. */
export const TAG_LINKED_RECORDS_STATUS = Object.freeze({
    LOADING: 'loading',
    LOADED: 'loaded',
    ERROR: 'error',
});

// Tags whose linked records stay cached. Panels open and close all session and several can be
// open at once, so the map is capped rather than left to grow; five covers the side-by-side case.
const LINKED_RECORDS_CACHE_LIMIT = 5;

// In-flight fetches by tenant/actor/tag/view, so two readers of the same admitted view (the Records tab and the
// register-linked field icons) share one request. Deliberately outside the store: a pending
// promise is plumbing, not state, and reactive tracking of it buys nothing.
const linkedRecordsRequests = new Map();
let latestSpatialNavigationRequestId = 0;

function facilityHeaders() {
    const facilityId = store.state.assetId;
    return facilityId ? { 'X-Facility-Id': facilityId } : {};
}

/**
 * `lockedRegisters` and `linkedFieldKeys` arrive with the API step that follows this one, so a
 * server that predates it simply omits them. Defaulting them once, here, keeps every reader off
 * `undefined` guards and gives the tab and the field icons the same shape to read.
 */
function normalizeLinkedRecordsPayload(payload) {
    return {
        ...payload,
        lockedRegisters: Array.isArray(payload.lockedRegisters) ? payload.lockedRegisters : [],
        linkedFieldKeys: Array.isArray(payload.linkedFieldKeys) ? payload.linkedFieldKeys : [],
    };
}

function linkedRecordsErrorMessage(err) {
    const data = err?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    return data?.message || err?.message || 'Failed to load the records linked to this tag.';
}

/**
 * In-flight `GET_BASE_TAG_BY_ID` reads, keyed by tag + facility + expected kind. Entries live only
 * while a request is outstanding; see the action for why this is a request collapse, not a cache.
 */
const baseTagReadsInFlight = new Map();

function hydrateBaseTag(data) {
    let Model;
    switch (data?.discriminator) {
        case 'ForgeAnomaly': Model = ForgeAnomaly; break;
        case 'ForgeEquipment': Model = ForgeEquipment; break;
        case 'ForgeStructural': Model = ForgeStructural; break;
        case 'ForgePipeline': Model = ForgePipeline; break;
        case 'ForgeScaffold': Model = ForgeScaffold; break;
        case 'ForgeAnnotation': Model = ForgeAnnotation; break;
        case 'ForgeInspection': Model = ForgeInspection; break;
        case 'ForgeAvaInspect': Model = ForgeAvaInspect; break;
        default: throw new TypeError('Unsupported base tag discriminator');
    }
    const tag = Object.assign(new Model(), data);
    tag.parseContent();
    return tag;
}

function readBaseTagFresh(request) {
    const isScopedRequest = request != null && typeof request === 'object' && !Array.isArray(request);
    const id = isScopedRequest ? request.id : request;
    const assetId = isScopedRequest && Object.prototype.hasOwnProperty.call(request, 'assetId')
        ? request.assetId
        : router.currentRoute.params.id || null;
    const expectedKind = isScopedRequest ? request.expectedKind : null;
    const scope = captureMutationScope(assetId);
    const authority = captureTagGeometryAuthority(id, scope.facilityId);
    return HttpService.get({
        url: 'api/tags/getBaseTagById',
        headers: assetId ? { 'X-Facility-Id': assetId } : facilityHeaders(),
        params: { id, assetId, expectedKind },
    }).then(({ data }) => {
        if (!data) throw new TypeError('Base tag response is empty');
        const tag = hydrateBaseTag(data);
        recordAuthoritativeTagGeometry(tag, scope, authority);
        return tag;
    }).catch(err => {
        debugError('❌ Fetch base tag by id: ', err);
        throw err;
    });
}

function mutationReceipt({ operation, tagId, facilityId, outcome, tag, authoritativePoint, scope }) {
    const input = {
        operation,
        tagId,
        facilityId,
        outcome,
        confirmedAbsentInScope: false,
    };
    if (scope) {
        input.companyId = scope.companyId;
        input.principalId = scope.principalId;
    }
    if (authoritativePoint !== undefined) input.authoritativePoint = authoritativePoint;
    else if (tag) input.authoritativePoint = resolveTagMarkerPoint(tag);
    return createTagMarkerLifecycleReceipt(input);
}

function captureMutationScope(facilityId) {
    return Object.freeze({
        facilityId: String(facilityId ?? store.state.assetId ?? ''),
        companyId: String(store.getters?.['ACCOUNT/GET_COMPANY']?.id ?? ''),
        principalId: String(store.getters?.['ACCOUNT/GET_USER']?.id ?? ''),
    });
}

function isMutationScopeCurrent(scope) {
    return String(store.state.assetId ?? '') === scope.facilityId
        && String(store.getters?.['ACCOUNT/GET_COMPANY']?.id ?? '') === scope.companyId
        && String(store.getters?.['ACCOUNT/GET_USER']?.id ?? '') === scope.principalId;
}

function captureTagGeometryAuthority(tagId, facilityId) {
    const manager = store.state.forgeService?.panoramaHotspotManager;
    return Object.freeze({
        manager,
        decision: manager?.getTagMarkerGeometryDecision?.(tagId, facilityId) ?? null,
    });
}

function recordAuthoritativeTagGeometry(tag, scope, authority) {
    if (!tag || !isMutationScopeCurrent(scope)) return false;
    const manager = store.state.forgeService?.panoramaHotspotManager;
    if (!manager || manager !== authority.manager) return false;
    const receipt = mutationReceipt({
        operation: 'authoritative-refresh',
        tagId: tag.id,
        facilityId: scope.facilityId,
        outcome: TAG_MARKER_OUTCOME.CONFIRMED_SUCCESS,
        tag,
    });
    return manager.recordAuthoritativeTagGeometry?.(
        receipt, authority.decision, { render: false },
    ) === true;
}

function captureTagGeometryAuthorities(tagIds, facilityId) {
    return new Map(uniqueTagIds(tagIds).map(tagId => [
        tagId,
        captureTagGeometryAuthority(tagId, facilityId),
    ]));
}

function recordCatalogTagGeometry(catalog, scope, authorities) {
    if (!isMutationScopeCurrent(scope) || !Array.isArray(catalog)) return;
    catalog.forEach(tag => {
        const authority = authorities.get(String(tag?.id));
        if (!authority) return;
        const hasGeometry = Object.prototype.hasOwnProperty.call(tag, 'point')
            || Object.prototype.hasOwnProperty.call(tag, 'position');
        if (hasGeometry) recordAuthoritativeTagGeometry(tag, scope, authority);
    });
}

async function reconcileCreatedTag({ tag, scope, commit, mutation, dispatch }) {
    if (!isMutationScopeCurrent(scope)) return false;
    commit(mutation, tag);
    const receipt = mutationReceipt({
        operation: 'create', tagId: tag.id, facilityId: scope.facilityId,
        outcome: TAG_MARKER_OUTCOME.CONFIRMED_SUCCESS, tag, scope,
    });
    await dispatch('RECONCILE_TAG_MUTATION_RECEIPTS', [receipt], { root: true });
    return true;
}

function requireCatalogArray(data, label) {
    if (!Array.isArray(data)) throw new TypeError(`${label} catalog response must be an array`);
    return data;
}

function shortCatalog(data, type = null) {
    return requireCatalogArray(data, type || 'Tag').map(row => {
        const tag = Object.assign(new ForgeTagShortInfo(type || undefined), row);
        tag.parseContent();
        return tag;
    });
}

function uniqueTagIds(ids) {
    return [...new Set((Array.isArray(ids) ? ids : [ids])
        .filter(id => id != null)
        .map(id => String(id)))];
}

function deleteReceipts(ids, facilityId, catalog = [], authoritative = false) {
    return createCatalogReconciliationReceipts({
        operation: 'delete',
        tagIds: ids,
        facilityId,
        catalog,
        authoritative,
    });
}

function hydrateCatalog(data, Model, label) {
    return requireCatalogArray(data, label).map(row => {
        const tag = Object.assign(new Model(), row);
        tag.parseContent();
        return tag;
    });
}

const writeSucceededButRefreshFailed = 'The change was saved, but the refreshed tag could not be loaded. Reload this facility before making another change.';

/**
 * The one piece of error copy behind every tag position/harvest write.
 *
 * The three writer families report failure in three shapes, and this reads all of them: the
 * Master routes answer a ProblemDetails object, the narrow annotation / inspection / AVA
 * Inspect routes answer a PLAIN STRING body on a refusal and NOTHING at all on a 404, and the
 * anomaly route answers `{ code }` with no prose (AnomaliesTagsController.ToNativeWriteResponse).
 *
 * `err.message` is deliberately NOT in the chain. It is axios's own text, so the two bodiless
 * shapes - the empty 404 and the anomaly `{ code }` - would surface to the user as "Request
 * failed with status code 404", and a dropped connection as "Network Error". Every caller
 * passes a fallback that names the action instead.
 */
function tagMutationErrorMessage(err, fallback) {
    const data = err?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    return data?.message || data?.detail || data?.title || fallback;
}

/** The narrow per-type position routes. Never used for anything but the placement. */
const NARROW_POSITION_ROUTES = Object.freeze({
    annotation: id => `api/tags/annotations/${id}/position`,
    inspection: id => `api/tags/inspections/${id}/position`,
    avaInspect: id => `api/tags/ava-inspect/${id}/position`,
});

/**
 * The one implementation behind the narrow position writes below.
 *
 * NEVER REJECTS - callers apply `tag` only when `status === 'refreshed'`. The server answers with
 * the tag resolved for `assetId`; a body without an id is an acknowledgement, so the tag is
 * refetched through the facility-scoped `GET_BASE_TAG_BY_ID` (the per-type GETs are not
 * facility-scoped and would answer with another facility's placement).
 *
 * @param {Object} options
 * @param {Function} options.request - Issues the HTTP call; resolves with the axios response.
 * @param {Function} options.Model - Client model the response is hydrated into.
 * @returns {Promise<{status: string, tag?: Object, error?: Error}>}
 */
async function narrowPositionWrite({
    dispatch,
    commit,
    request,
    Model,
    tagId,
    assetId,
    operation,
    successMessage,
    failureMessage,
    logTag,
}) {
    const scope = captureMutationScope(assetId);
    let data;
    try {
        ({ data } = await request());
    } catch (err) {
        if (!isMutationScopeCurrent(scope)) return { status: 'scope-changed' };
        debugError(`${logTag} write FAILED:`, err);
        Vue.prototype.$dialog.notify.error(tagMutationErrorMessage(err, failureMessage), {
            position: 'top-right',
            timeout: 5000,
        });
        return {
            status: 'failed',
            error: err,
            receipt: mutationReceipt({
                operation,
                tagId,
                facilityId: assetId,
                outcome: TAG_MARKER_OUTCOME.CONFIRMED_FAILURE,
                scope,
            }),
        };
    }

    if (!isMutationScopeCurrent(scope)) return { status: 'scope-changed' };

    try {
        let tag;
        if (data?.id) {
            tag = Object.assign(new Model(), data);
            tag.parseContent();
        } else {
            tag = await dispatch('GET_BASE_TAG_BY_ID_FRESH', { id: tagId, assetId, expectedKind: null });
        }
        Vue.prototype.$dialog.notify.success(successMessage, {
            position: 'top-right',
            timeout: 3000,
        });
        const receipt = mutationReceipt({
            operation,
            tagId,
            facilityId: assetId,
            outcome: TAG_MARKER_OUTCOME.CONFIRMED_SUCCESS,
            tag,
            scope,
        });
        commit?.('APPLY_AUTHORITATIVE_TAG_REFRESH', tag, { root: true });
        await dispatch('RECONCILE_TAG_MUTATION_RECEIPTS', [receipt]);
        return {
            status: 'refreshed',
            tag,
            receipt,
        };
    } catch (err) {
        if (!isMutationScopeCurrent(scope)) return { status: 'scope-changed' };
        debugError(`${logTag} write succeeded but the refresh failed`, err);
        Vue.prototype.$dialog.notify.warning(writeSucceededButRefreshFailed, {
            position: 'top-right',
            timeout: 7000,
        });
        const receipt = mutationReceipt({
            operation,
            tagId,
            facilityId: assetId,
            outcome: TAG_MARKER_OUTCOME.WRITE_SUCCEEDED_REFRESH_FAILED,
            authoritativePoint: null,
            scope,
        });
        await dispatch('RECONCILE_TAG_MUTATION_RECEIPTS', [receipt]);
        return {
            status: TAG_MARKER_OUTCOME.WRITE_SUCCEEDED_REFRESH_FAILED,
            error: err,
            receipt,
        };
    }
}

async function refreshTagAfterWrite(dispatch, commit, tagId, forgeAssetId, operation, successMessage, scope) {
    if (!isMutationScopeCurrent(scope)) return { status: 'scope-changed' };
    try {
        const tag = await dispatch('GET_BASE_TAG_BY_ID_FRESH', {
            id: tagId,
            assetId: forgeAssetId,
            expectedKind: null,
        });
        Vue.prototype.$dialog.notify.success(successMessage, {
            position: 'top-right',
            timeout: 3000,
        });
        const receipt = mutationReceipt({
            operation,
            tagId,
            facilityId: forgeAssetId,
            outcome: TAG_MARKER_OUTCOME.CONFIRMED_SUCCESS,
            tag,
            scope,
        });
        commit?.('APPLY_AUTHORITATIVE_TAG_REFRESH', tag, { root: true });
        // A master that was neither harvested nor positioned is not in its catalog yet, and the
        // refresh above only patches - add it so it draws in every scan before the reload (AVA-624).
        commit?.('ADD_PLACED_MASTER_TO_CATALOG', tag, { root: true });
        await dispatch('RECONCILE_TAG_MUTATION_RECEIPTS', [receipt]);
        return {
            status: 'refreshed',
            tag,
            receipt,
        };
    } catch (err) {
        if (!isMutationScopeCurrent(scope)) return { status: 'scope-changed' };
        debugError('[TagPosition] Write succeeded but authoritative refresh failed', err);
        Vue.prototype.$dialog.notify.warning(writeSucceededButRefreshFailed, {
            position: 'top-right',
            timeout: 7000,
        });
        const receipt = mutationReceipt({
            operation,
            tagId,
            facilityId: forgeAssetId,
            outcome: TAG_MARKER_OUTCOME.WRITE_SUCCEEDED_REFRESH_FAILED,
            authoritativePoint: null,
            scope,
        });
        await dispatch('RECONCILE_TAG_MUTATION_RECEIPTS', [receipt]);
        return {
            status: TAG_MARKER_OUTCOME.WRITE_SUCCEEDED_REFRESH_FAILED,
            error: err,
            receipt,
        };
    }
}

export const TAGS = {
    state: {
        tagsSearchText: '',
        editingTag: null,
        addingTagInfo: null,
        isTagReadOnly: true,
        previousTags: [],
        tagsActivities: {
            byActivityNumber: {},
            byTagId: {}
        },
        linkedTagsPopupCallback: null,
        tagDynamicGraphs: null,
        documentsPanelCallback: null,

        // Tag Info Panel tab override: null (use the default 'info') or a one-shot request naming
        // its tag, { tab, tagId, onOpen } - see features/Tags/utils/tagInfoTabRequest.js.
        tagInfoDefaultTab: null,

        // Active document for ViewersSection Document tab
        activeDocument: null,

        // Register rows linked to a tag, keyed by tenant/actor/tag/view (AVA-493 Records tab): key ->
        // { status, payload, error }. Keyed rather than one shared slot because the info panels
        // read a global singleton tag and can be open beside one another, so an unkeyed cache
        // would serve one tag's records inside another tag's panel.
        linkedRecordsByTagId: {},

        ignoredTagsIds: [],
        mutableIgnoredTagsIds: [],
        tagHarvesterInfo: {
            id: null,
            title: "",
            filterOptions: [],
            markIgnore: false,
            removeSlash: false,
            selectedTreeLevel: "All Tiers",
            matchingCategory: "",
            matchingProperty: "",
        },

        filteredTags: [],

        // Tag Harvester
        harvesters: [],
        harvestedNodesIds: [],
        fillingHarvesterTagsThumbnails: false,
        harvesterTagsToCapture: [],

        // Anomaly Tags
        allExistingAnomaliesDbIds: [],
        anomaliesCount: 0,
        filteredAnomalyTagsIds: [],

        // Structural Tags
        manualStructuralTags: [],
        structuralTags: [],
        allExistingStructuralDbIds: [],
        structuralTagsCount: 0,
        filteredStructuralTagsIds: [],

        // Pipeline Tags
        manualPipelinesTags: [],
        pipelinesTags: [],
        allExistingPipelineDbIds: [],
        pipelinesTagsCount: 0,
        filteredPipelineTagsIds: [],

        // AVA Inspect Tags
        avaInspectTags: [],
        avaInspectTagsCount: 0,
        filteredAvaInspectTagsIds: [],

        //automatic tags
        automaticTags: [],
        allExistingAutomaticDbids: [],//[{id:1, dbid:254,name:xx, thumbnailUrl:xxx,objectName=xx},........]
        allAutmaticTagsCount: 0,
        filteredAutomaticTagsIds: [],
        fillingAutomaticTagsImages: false,
        selectedAutomaticTagsGroup: null,
        requestedImagesId: [],
        themedDbIds: [],
        showPopulateAutomaticTagsPopup: false,
        populateAutomaticTagsGroup: null,
        fillingInspectionsImages: false,
        inspectionDbIdsToCapture: [],

        getBaseTagsTimeout: null,

        //AMIS Tags
        amisToken: "",
        lastAMISAdvancedFetchDate: 0,
        amisSearchText: "",
        inspectionIdsFilter: [],
        amisUnits: [],
        inspectionSelectedUnits: [],
        amisTags: [],
        inspectionTagTree: [],
        selectedInspectionTreeTag: null,
        allAMISFilteredInspections: [], // Only filtered AMIS Inspections with available point.
        allExistingAMISDbids: [],//[{id:1,dbid:22 ,children:[],cr:1,rl:1,rt:1,pltName:xx},........]
        allAMISTagsCount: 0,
        allAMISTagsSearchCount: 0,
        isAMISTagReadOnly: true,
        editingAMISTag: null,
        // AVA inspections have their own detail panel and selection lifecycle. Keep this
        // separate from standard inspections so both panels can stay open concurrently.
        isAvaInspectTagReadOnly: true,
        editingAvaInspectTag: null,
        showAdminSyncPopup: false,
        fillingAMISTagsImages: false,
        sortingData: [
            {
                id: 'Name',
                title: 'Tag Name',
                isSelected: true,
                isExpanded: false,
                isAsc: true,
            },
            {
                id: 'CreatedDate',
                title: 'Date Added',
                isSelected: false,
                isExpanded: false,
                isAsc: true,
            }
        ],
        // Static default inspection bands (CR / RL / RT) — extracted to a store-free constants module
        // so the pure coloring-schema modules can consume them. Cloned per field so the in-place
        // `LOAD_AMIS_COLORS` overlay mutates the store's own copies, never the frozen source.
        InspectionRemainingLifeColorOptions: REMAINING_LIFE_BANDS.map((b) => ({ ...b })),
        InspectionThickPlanColorOptions: THICKNESS_BANDS.map((b) => ({ ...b })),
        InspectionCorrRateColorOptions: CORROSION_RATE_BANDS.map((b) => ({ ...b })),
        LastInspectionColorOptions: [
            {
                "code": 5,
                "codeOrdr": 1,
                "judgSymb": "Less than a year",
                "judgCor": "#1153FF",
                "judgCrr1": 365,
                "judgMkr1": "<",
                "judgCrr2": 0,
                "judgMkr2": ">=",
                "title": "Less than a year"
            },
            {
                "code": 5,
                "codeOrdr": 2,
                "judgSymb": "Less than 5 years",
                "judgCor": "#65AC50",
                "judgCrr1": 1825,
                "judgMkr1": "<",
                "judgCrr2": 365,
                "judgMkr2": ">=",
                "title": "Less than 5 years"
            },
            {
                "code": 5,
                "codeOrdr": 3,
                "judgSymb": "Less than 10 years",
                "judgCor": "#f3ff00",
                "judgCrr1": 3650,
                "judgMkr1": "<",
                "judgCrr2": 1825,
                "judgMkr2": ">=",
                "title": "Less than 10 years"
            },
            {
                "code": 5,
                "codeOrdr": 4,
                "judgSymb": "Less than 15 years",
                "judgCor": "#FF5C0C",
                "judgCrr1": 5475,
                "judgMkr1": "<",
                "judgCrr2": 3650,
                "judgMkr2": ">=",
                "title": "Less than 15 years"
            },
            {
                "code": 5,
                "codeOrdr": 5,
                "judgSymb": "Over 15 years",
                "judgCor": "#ff0000",
                "judgCrr1": "∞",
                "judgMkr1": "<",
                "judgCrr2": 5475,
                "judgMkr2": ">=",
                "title": "Over 15 years"
            },
            {
                "code": 5,
                "codeOrdr": 7,
                "judgSymb": 'No Record',
                "judgCor": "#0f7968",
                "judgCrr1": null,
                "judgMkr1": null,
                "judgCrr2": null,
                "judgMkr2": null,
                "title": "No Record"
            }
        ],
        NextInspectionColorOptions: [
            {
                "code": 6,
                "codeOrdr": 1,
                "judgSymb": "Overdue",
                "judgCor": "#ff0000",
                "judgCrr1": 0,
                "judgMkr1": "<",
                "judgCrr2": "-∞",
                "judgMkr2": ">",
                "title": "Overdue"
            },
            {
                "code": 6,
                "codeOrdr": 2,
                "judgSymb": "Less than 1 year",
                "judgCor": "#FF5C0C",
                "judgCrr1": 365,
                "judgMkr1": "<",
                "judgCrr2": 0,
                "judgMkr2": ">=",
                "title": "Less than 1 year"
            },
            {
                "code": 6,
                "codeOrdr": 3,
                "judgSymb": "Less than 4 years",
                "judgCor": "#f3ff00",
                "judgCrr1": 1825,
                "judgMkr1": "<",
                "judgCrr2": 365,
                "judgMkr2": ">=",
                "title": "Less than 4 years"
            },
            {
                "code": 6,
                "codeOrdr": 4,
                "judgSymb": "Over 4 years",
                "judgCor": "#65AC50",
                "judgCrr1": "∞",
                "judgMkr1": "<",
                "judgCrr2": 1825,
                "judgMkr2": ">=",
                "title": "Over 4 years"
            },
            {
                "code": 6,
                "codeOrdr": 5,
                "judgSymb": 'No Plan',
                "judgCor": "#0f7968",
                "judgCrr1": null,
                "judgMkr1": null,
                "judgCrr2": null,
                "judgMkr2": null,
                "title": "No Plan"
            }
        ],
        InspectionPlanColorOptions: [
            {
                "code": 4,
                "codeOrdr": 1,
                "judgSymb": "Over a year",
                "judgCor": "#65AC50",
                "judgCrr1": "∞",
                "judgMkr1": "<",
                "judgCrr2": 365,
                "judgMkr2": ">=",
                "title": "Over a year"
            },
            {
                "code": 4,
                "codeOrdr": 2,
                "judgSymb": "Less than a year",
                "judgCor": "#FF5C0C",
                "judgCrr1": 365,
                "judgMkr1": "<",
                "judgCrr2": 0,
                "judgMkr2": ">=",
                "title": "Less than a year"
            },
            {
                "code": 4,
                "codeOrdr": 3,
                "judgSymb": "Overdue",
                "judgCor": "#ff0000",
                "judgCrr1": 0,
                "judgMkr1": "<",
                "judgCrr2": "-∞",
                "judgMkr2": ">",
                "title": "Overdue"
            },
            {
                "code": 4,
                "codeOrdr": 4,
                "judgSymb": 'Next Inspection date not provided',
                "judgCor": "#0f7968",
                "judgCrr1": null,
                "judgMkr1": null,
                "judgCrr2": null,
                "judgMkr2": null,
                "title": "Next Inspection date not provided"
            }
        ],
        AIInspectionColorOptions: [
            {
                "title": "Severity 1",
                "judgSymb": 'Level 1',
                "judgCor": "#215F9B",
                "judgCrr1": 1,
                "judgMkr1": "==",
                "judgCrr2": 1,
                "judgMkr2": "==",
                "value": 1,
                "code": 1,
                "codeOrdr": 1
            },
            {
                "title": "Severity 2",
                "judgSymb": 'Level 2',
                "judgCor": "#D2A000",
                "judgCrr1": 2,
                "judgMkr1": "==",
                "judgCrr2": 2,
                "judgMkr2": "==",
                "value": "2",
                "code": 2,
                "codeOrdr": 2
            },
            {
                "title": "Severity 3",
                "judgSymb": 'Level 3',
                "judgCor": "#C04F15",
                "judgCrr1": 3,
                "judgMkr1": "==",
                "judgCrr2": 3,
                "judgMkr2": "==",
                "value": "3",
                "code": 3,
                "codeOrdr": 3
            },
            {
                "title": "Severity 4",
                "judgSymb": 'Level 4',
                "judgCor": "#A80000",
                "judgCrr1": 4,
                "judgMkr1": "==",
                "judgCrr2": 4,
                "judgMkr2": "==",
                "value": "4",
                "code": 4,
                "codeOrdr": 4
            },
            {
                "title": "N/A",
                "judgSymb": 'N/A',
                "value": null,
                "judgCrr1": null,
                "judgMkr1": null,
                "judgCrr2": null,
                "judgMkr2": null,
                "judgCor": "#03624c",
                "code": 5,
                "codeOrdr": 5
            }
        ],

        // Coating Condition Filter (Ri0-Ri5) - NEW FILTER
        // TODO: Backend Integration - coatingCondition property needs to be returned from API
        CoatingConditionColorOptions: [
            {
                "code": 7,
                "codeOrdr": 1,
                "judgSymb": "Ri0",
                "judgCor": "#4CAF50",
                "judgCrr1": 'ri0',
                "judgMkr1": '==',
                "judgCrr2": 'ri0',
                "judgMkr2": '==',
                "title": "Ri0 (0% rusted)",
                "tooltip": "No visible rust"
            },
            {
                "code": 7,
                "codeOrdr": 2,
                "judgSymb": "Ri1",
                "judgCor": "#8BC34A",
                "judgCrr1": 'ri1',
                "judgMkr1": '==',
                "judgCrr2": 'ri1',
                "judgMkr2": '==',
                "title": "Ri1 (0.05% rusted)",
                "tooltip": "Spot rust, <0.05% surface area"
            },
            {
                "code": 7,
                "codeOrdr": 3,
                "judgSymb": "Ri2",
                "judgCor": "#CDDC39",
                "judgCrr1": 'ri2',
                "judgMkr1": '==',
                "judgCrr2": 'ri2',
                "judgMkr2": '==',
                "title": "Ri2 (0.5% rusted)",
                "tooltip": "Spot rust, 0.5% surface area"
            },
            {
                "code": 7,
                "codeOrdr": 4,
                "judgSymb": "Ri3",
                "judgCor": "#FFEB3B",
                "judgCrr1": 'ri3',
                "judgMkr1": '==',
                "judgCrr2": 'ri3',
                "judgMkr2": '==',
                "title": "Ri3 (1% rusted)",
                "tooltip": "Spot rust, ~1% surface area"
            },
            {
                "code": 7,
                "codeOrdr": 5,
                "judgSymb": "Ri4",
                "judgCor": "#FF9800",
                "judgCrr1": 'ri4',
                "judgMkr1": '==',
                "judgCrr2": 'ri4',
                "judgMkr2": '==',
                "title": "Ri4 (8-39% rusted)",
                "tooltip": "Significant rust coverage"
            },
            {
                "code": 7,
                "codeOrdr": 6,
                "judgSymb": "Ri5",
                "judgCor": "#F44336",
                "judgCrr1": 'ri5',
                "judgMkr1": '==',
                "judgCrr2": 'ri5',
                "judgMkr2": '==',
                "title": "Ri5 (40-50% rusted)",
                "tooltip": "Heavy rust coverage"
            },
            {
                "code": 7,
                "codeOrdr": 7,
                "judgSymb": "N/A",
                "judgCor": "#9E9E9E",
                "judgCrr1": null,
                "judgMkr1": null,
                "judgCrr2": null,
                "judgMkr2": null,
                "title": "N/A",
                "tooltip": "No coating assessment available"
            }
        ],

        // Corrosion Condition Filter (Cat A-D) - NEW FILTER
        // Uses existing categoryId property from ForgeAvaInspect model
        CorrosionConditionColorOptions: [
            {
                "code": 8,
                "codeOrdr": 1,
                "judgSymb": "Cat A",
                "judgCor": "#4CAF50",
                "judgCrr1": 'catA',
                "judgMkr1": '==',
                "judgCrr2": 'catA',
                "judgMkr2": '==',
                "title": "Cat A (Light Surface)",
                "tooltip": "Light surface corrosion, ~2mm thickness loss",
                "description": "Isolated pitting, minimal scale"
            },
            {
                "code": 8,
                "codeOrdr": 2,
                "judgSymb": "Cat B",
                "judgCor": "#FFEB3B",
                "judgCrr1": 'catB',
                "judgMkr1": '==',
                "judgCrr2": 'catB',
                "judgMkr2": '==',
                "title": "Cat B (Moderate)",
                "tooltip": "Moderate corrosion, ~4mm thickness loss",
                "description": "Scattered pitting, moderate scale"
            },
            {
                "code": 8,
                "codeOrdr": 3,
                "judgSymb": "Cat C",
                "judgCor": "#FF9800",
                "judgCrr1": 'catC',
                "judgMkr1": '==',
                "judgCrr2": 'catC',
                "judgMkr2": '==',
                "title": "Cat C (Heavy Corrosion)",
                "tooltip": "Heavy corrosion, ~6mm thickness loss",
                "description": "Clustered pitting, heavy scale"
            },
            {
                "code": 8,
                "codeOrdr": 4,
                "judgSymb": "Cat D",
                "judgCor": "#F44336",
                "judgCrr1": 'catD',
                "judgMkr1": '==',
                "judgCrr2": 'catD',
                "judgMkr2": '==',
                "title": "Cat D (Severe Corrosion)",
                "tooltip": "Severe corrosion, 9mm+ thickness loss",
                "description": "Extensive pitting, severe scale"
            },
            {
                "code": 8,
                "codeOrdr": 5,
                "judgSymb": "N/A",
                "judgCor": "#9E9E9E",
                "judgCrr1": null,
                "judgMkr1": null,
                "judgCrr2": null,
                "judgMkr2": null,
                "title": "N/A",
                "tooltip": "No corrosion assessment available"
            }
        ],

        // Defect Severity Filter - NEW FILTER
        // TODO: Backend Integration - defectSeverity property needs to be returned from API
        DefectSeverityColorOptions: [
            {
                "code": 9,
                "codeOrdr": 1,
                "judgSymb": "Critical",
                "judgCor": "#A80000",
                "judgCrr1": 'criticalDefect',
                "judgMkr1": '==',
                "judgCrr2": 'criticalDefect',
                "judgMkr2": '==',
                "title": "Critical Defect",
                "tooltip": "Immediate safety risk or imminent failure",
                "priority": 1
            },
            {
                "code": 9,
                "codeOrdr": 2,
                "judgSymb": "Priority",
                "judgCor": "#e27640ff",
                "judgCrr1": 'priorityDefect',
                "judgMkr1": '==',
                "judgCrr2": 'priorityDefect',
                "judgMkr2": '==',
                "title": "Priority Defect",
                "tooltip": "High risk requiring prompt action within 30 days",
                "priority": 2
            },
            {
                "code": 9,
                "codeOrdr": 3,
                "judgSymb": "Defect",
                "judgCor": "#ffbc03ff",
                "judgCrr1": 'defect',
                "judgMkr1": '==',
                "judgCrr2": 'defect',
                "judgMkr2": '==',
                "title": "Defect",
                "tooltip": "Requires repair but not immediately critical",
                "priority": 3
            },
            {
                "code": 9,
                "codeOrdr": 4,
                "judgSymb": "Minor",
                "judgCor": "#3ff0afff",
                "judgCrr1": 'minorImpairment',
                "judgMkr1": '==',
                "judgCrr2": 'minorImpairment',
                "judgMkr2": '==',
                "title": "Minor Impairment",
                "tooltip": "Minor degradation, monitor during next inspection",
                "priority": 4
            },
            {
                "code": 9,
                "codeOrdr": 5,
                "judgSymb": "Good",
                "judgCor": "#036b44ff",
                "judgCrr1": 'goodCondition',
                "judgMkr1": '==',
                "judgCrr2": 'goodCondition',
                "judgMkr2": '==',
                "title": "Good Condition",
                "tooltip": "No defects identified, no action required",
                "priority": 5
            },
            {
                "code": 9,
                "codeOrdr": 6,
                "judgSymb": "N/A",
                "judgCor": "#9E9E9E",
                "judgCrr1": null,
                "judgMkr1": null,
                "judgCrr2": null,
                "judgMkr2": null,
                "title": "N/A",
                "tooltip": "No severity assessment available"
            }
        ],

        // Zones for spatial filtering - NEW FILTER
        // TODO: Backend Integration - Load from GET /api/assets/{assetId}/zones
        avaInspectZones: [],

        InspectionSelectedFilter: 'No Filter',
        // NEW - Separate filter selections for dual filtering (renamed for clarity)
        InspectionsSelectedFilter: 'No Filter',
        AvaInspectionsSelectedFilter: 'No Filter',
        // NEW - Separate color options for each filter type
        InspectionsAvailableColorOptions: [],
        AvaInspectionsAvailableColorOptions: [],
        inspectionsSelectedColorOptions: [],
        avaInspectionsSelectedColorOptions: [],
        // NEW - Which filter sources are enabled in UI/API
        filterSources: { inspections: true, avaInspections: true },
        inspectionType: 'Thickness',
        inspectionTempPresSelectedFilter: 'Temperature',
        InspectionFiltersOptions: [
            "No Filter",
            'Remaining Thickness',
            'Remaining Life',
            'Corrosion Rate',
            'Last Inspection (Thickness)',
            'Last Inspection (Visual)',
            'Last Inspection (Both)',
            'Next Inspection (Thickness)',
            'Next Inspection (Visual)',
            'Next Inspection (Both)',
            'AI Inspection',
            'Inspection Tag',
        ],
        inspectionMasterTypeOptions: [
            // { id: 0, name: 'Select All', isChecked: true },
            { id: 1, name: 'Equipment', isChecked: true },
            { id: 2, name: 'Pipeline', isChecked: true },
            { id: 3, name: 'Structural', isChecked: true },
            { id: 4, name: 'None', isChecked: true }
        ],
        inspectionFiltersOptionsWithCheck: [
            { name: 'No Filter', isChecked: true },
            { name: 'Remaining Thickness', isChecked: false },
            { name: 'Last Inspection', isChecked: false, needType: true },
            { name: 'Next Inspection', isChecked: false, needType: true },
            { name: 'Remaining Life', isChecked: false, tempPresFilter: true, codeFilter: true },
            { name: 'Corrosion Rate', isChecked: false, tempPresFilter: true, codeFilter: true },
            { name: 'AI Inspection', isChecked: false },
            // NEW FILTERS - Remote Condition Assessment
            { name: 'Coating Condition', isChecked: false, needType: false, code: 7 },
            { name: 'Corrosion Condition', isChecked: false, needType: false, code: 8 },
            { name: 'Severity', isChecked: false, needType: false, code: 9 },
        ],
        InspectionAvailableColorOptions: [],
        selectedColorOptions: [],
        filteredInspection: [],
        isLoadingAMISTags: false,
        showTagMasterResult: false,
        isEquipmentMasterAvailable: false,
        isPipelineMasterAvailable: false,
        isStructuralMasterAvailable: false,
        isInspectionTreeOption: false,
        // Master Tag and Date Filters
        selectedMasterTagId: null,
        // Doc-viewer focus-mode context: true while a master tag info panel is
        // open. Drives Quick Settings "Focus" toggle visibility (set by
        // docLinkIsolationMixin). See DOC_VIEWER_LINK_ENHANCEMENTS.md.
        isTagFocusContextActive: false,
        dateFilterType: 'Year', // 'Year', 'Month', 'Day', 'Range'
        dateFilterValue: null, // Date value or range object
        customFilters: [], // [{ property, values, searchText, dateFrom, dateTo, min, max }]

        // CML Navigation - Used to highlight specific CML when navigating from popup
        selectedCmlId: null,
        // TML Navigation - highlight a specific TmlPoint row when navigating from the TML popup (AVA-437 Tier 2)
        selectedTmlPointId: null,
        // CML Isolation - When set, only these CML IDs are shown as markers
        isolatedCmlIds: [],
        // CML marker ownership — id of the surface that last rendered the shared, global
        // CML marker layer (last-writer-wins). A surface derives its own truthful switch
        // state from this (see InspectionFiltersDrawer `markersActive`). null = no owner.
        cmlMarkerOwner: null,
    },

    mutations: {
        APPLY_AUTHORITATIVE_TAG_REFRESH(state, refreshedTag) {
            if (!refreshedTag?.id) return;
            const buckets = [
                state.automaticTags,
                state.allExistingAutomaticDbids,
                state.manualStructuralTags,
                state.structuralTags,
                state.allExistingStructuralDbIds,
                state.manualPipelinesTags,
                state.pipelinesTags,
                state.allExistingPipelineDbIds,
                // Anomaly 3D icons and 360 hotspots draw from this bucket.
                state.allExistingAnomaliesDbIds,
                state.filteredTags,
                // Inspection rows (amisRow.vue via GET_AMIS_TAGS) and the inspection dbId
                // projection the AMIS renderers read.
                state.amisTags,
                state.allExistingAMISDbids,
                // AVA Inspect 3D icons, 360 hotspots and the context menu draw from THIS copy
                // (root getter GET_AVA_INSPECT_TAGS). The rows render from the AVA_INSPECT module's
                // own `avaInspectTags`, a different array, reached by applyAuthoritativeTagRefresh.
                state.avaInspectTags,
                // Root-state collections, not module state: the General and Inspection 3D icons and
                // 360 hotspots draw from `annotations` / `inspections`, which the old full-save
                // flows kept in step by splicing through updateAnnotation / updateInspections.
                // Reached through the store singleton the module already uses for facilityHeaders;
                // a bucket that is not an array (or a store that has none) is skipped below.
                store.state.annotations,
                store.state.inspections,
            ];
            buckets.forEach(bucket => {
                if (!Array.isArray(bucket)) return;
                bucket.forEach(tag => {
                    if (String(tag?.id) === String(refreshedTag.id)) applyTagPlacementFields(tag, refreshedTag);
                });
            });
            // Whitelist, not Object.assign: the refetch carries no relations, so a whole-object
            // assign would wipe comments/attachments/documents/linked tags from the open panel.
            if (String(state.editingTag?.id) === String(refreshedTag.id)) {
                applyTagPlacementFields(state.editingTag, refreshedTag);
            }
        },
        /**
         * Adds a just-placed master to its family catalog when the catalog does not have it yet
         * (see masterCatalogPlacement.js). Rows already present are left to
         * APPLY_AUTHORITATIVE_TAG_REFRESH, which patches them.
         */
        ADD_PLACED_MASTER_TO_CATALOG(state, refreshedTag) {
            const missing = missingMasterCatalogEntry(refreshedTag, state);
            if (!missing) return;
            const row = Object.assign(new ForgeTagShortInfo(missing.type), missing.fields);
            row.parseContent();
            state[missing.bucket].push(row);
        },
        SET_TAGS_SEARCH_TEXT(state, value) {
            state.tagsSearchText = value;
        },
        SET_EDITING_TAG(state, value) {
            state.editingTag = value;
        },
        SET_ADDING_TAG_INFO(state, value) {
            state.addingTagInfo = value;
        },
        SET_EDITING_AMIS_TAG(state, value) {
            state.editingAMISTag = value;
        },
        SET_EDITING_AVA_INSPECT_TAG(state, value) {
            state.editingAvaInspectTag = value;
        },
        SET_AMIS_TAGS(state, value) {
            state.amisTags = value;
        },
        SET_IS_TAG_READ_ONLY(state, value) {
            state.isTagReadOnly = value;
        },
        SET_TAG_INFO_DEFAULT_TAB(state, value) {
            state.tagInfoDefaultTab = normalizeTagInfoTabRequest(value);
        },
        SET_ACTIVE_DOCUMENT(state, value) {
            state.activeDocument = value;
        },
        // Writes one tag's linked-records entry and keeps the map at its cap. The written key is
        // re-inserted last so write order doubles as recency, and eviction takes the oldest keys
        // only — never the one just written, which a mounted panel is reading right now.
        SET_TAG_LINKED_RECORDS_ENTRY(state, { tagId, cacheKey, entry }) {
            const key = cacheKey || tagId;
            const next = { ...state.linkedRecordsByTagId };
            delete next[key];
            next[key] = entry;

            const overflow = Object.keys(next).length - LINKED_RECORDS_CACHE_LIMIT;
            if (overflow > 0) {
                Object.keys(next).slice(0, overflow).forEach(oldKey => {
                    if (oldKey !== key) delete next[oldKey];
                });
            }

            state.linkedRecordsByTagId = next;
        },
        SET_IS_AMIS_TAG_READ_ONLY(state, value) {
            state.isAMISTagReadOnly = value;
        },
        SET_IS_AVA_INSPECT_TAG_READ_ONLY(state, value) {
            state.isAvaInspectTagReadOnly = value;
        },
        SET_IGNORED_TAGS_IDS(state, value) {
            state.ignoredTagsIds = value;
        },
        SET_MUTABLE_IGNORED_TAGS_IDS(state, value) {
            state.mutableIgnoredTagsIds = value;
        },
        SET_TAG_HARVESTER_INFO(state, value) {
            state.tagHarvesterInfo = value;
        },
        SET_ANOMALIES_TAGS(state, value) {
            state.anomaliesTags = value;
        },
        SET_ALL_EXISTING_ANOMALIES_TAGS(state, value) {
            state.allExistingAnomaliesDbIds = value;
        },
        SET_ANOMALIES_TAGS_COUNT(state, value) {
            state.anomaliesTagsCount = value;
        },
        SET_FILTERED_ANOMALIES_TAGS_IDS(state, value) {
            state.filteredAnomalyTagsIds = value;
        },
        SET_MANUAL_STRUCTURAL_TAGS(state, value) {
            state.manualStructuralTags = value;
        },
        SET_STRUCTURAL_TAGS(state, value) {
            state.structuralTags = value;
        },
        SET_ALL_EXISTING_STRUCTURAL_TAGS(state, value) {
            state.allExistingStructuralDbIds = value;
        },
        SET_STRUCTURAL_TAGS_COUNT(state, value) {
            state.structuralTagsCount = value;
        },
        SET_FILTERED_STRUCTURAL_TAGS_IDS(state, value) {
            state.filteredStructuralTagsIds = value;
        },
        SET_MANUAL_PIPELINES_TAGS(state, value) {
            state.manualPipelinesTags = value;
        },
        SET_PIPELINES_TAGS(state, value) {
            state.pipelinesTags = value;
        },
        SET_ALL_EXISTING_PIPELINES_TAGS(state, value) {
            state.allExistingPipelineDbIds = value;
        },
        SET_PIPELINES_TAGS_COUNT(state, value) {
            state.pipelinesTagsCount = value;
        },
        SET_FILTERED_PIPELINES_TAGS_IDS(state, value) {
            state.filteredPipelineTagsIds = value;
        },
        SET_AVA_INSPECT_TAGS(state, value) {
            state.avaInspectTags = value;
        },
        SET_AVA_INSPECT_TAGS_COUNT(state, value) {
            state.avaInspectTagsCount = value;
        },
        SET_FILTERED_AVA_INSPECT_TAGS_IDS(state, value) {
            state.filteredAvaInspectTagsIds = value;
        },
        SET_AUTOMATIC_TAGS(state, value) {
            state.automaticTags = value;
        },
        SET_AUTOMATIC_TAGS_COUNT(state, value) {
            state.allAutmaticTagsCount = value;
        },
        SET_AMIS_TAGS_TOTAL_COUNT(state, value) {
            state.allAMISTagsCount = value;
        },
        SET_AMIS_TAGS_SEARCH_COUNT(state, value) {
            state.allAMISTagsSearchCount = value;
        },
        SET_SELECTED_AUTOMATIC_TAGS_GROUP(state, value) {
            state.selectedAutomaticTagsGroup = value;
        },
        SET_FILLING_AUTOMATIC_TAGS_IMAGES(state, value) {
            state.fillingAutomaticTagsImages = value;
        },
        SET_FILLING_HARVESTER_TAGS_THUMBNAILS(state, value) {
            state.fillingHarvesterTagsThumbnails = value;
        },
        SET_HARVESTER_TAGS_TO_CAPTURE(state, value) {
            state.harvesterTagsToCapture = value;
        },
        SET_FILLING_AMIS_TAGS_IMAGES(state, value) {
            state.fillingAMISTagsImages = value;
        },
        SET_FILLING_INSPECTIONS_IMAGES(state, value) {
            state.fillingInspectionsImages = value;
        },
        SET_INSPECTIONS_TO_CAPTURE(state, value) {
            state.inspectionDbIdsToCapture = value;
        },
        SET_LINKED_TAGS_CALLBACK(state, value) {
            state.linkedTagsPopupCallback = value;
        },
        SET_FILTERED_AUTOMATIC_TAGS_IDS(state, value) {
            state.filteredAutomaticTagsIds = value;
        },
        SET_AMIS_UNITS(state, value) {
            state.amisUnits = value;
        },
        // NEW - Set selected filter sources (inspections / avaInspections)
        SET_FILTER_SOURCES(state, value) {
            state.filterSources = { ...state.filterSources, ...value };
        },
        SET_INSPECTION_SELECTED_UNITS(state, value) {
            state.inspectionSelectedUnits = value;
        },
        // NEW MUTATIONS - Zones for spatial filtering
        SET_AVA_INSPECT_ZONES(state, zones) {
            state.avaInspectZones = zones.map(z => ({
                ...z,
                isChecked: true // Default: all zones selected
            }));
        },
        TOGGLE_ZONE_SELECTION(state, zoneId) {
            const zone = state.avaInspectZones.find(z => z.id === zoneId);
            if (zone) zone.isChecked = !zone.isChecked;
        },
        SET_REQUESTED_IMAGES_IDS(state, value) {
            state.requestedImagesId = value
        },
        SET_AMIS_TOKEN(state, value) {
            state.amisToken = value;
        },
        SET_ALL_AMIS_FILTERED_INSPECTIONS(state, value) {
            state.allAMISFilteredInspections = value;
        },
        UPSERT_ANOMALY_TAG(state, tag) {
            upsertTagById(state.allExistingAnomaliesDbIds, tag);
        },
        UPSERT_PIPELINE_TAG(state, tag) {
            upsertTagById(state.manualPipelinesTags, tag);
        },
        UPSERT_STRUCTURAL_TAG(state, tag) {
            upsertTagById(state.manualStructuralTags, tag);
        },
        UPSERT_AVA_INSPECT_TAG(state, tag) {
            upsertTagById(state.avaInspectTags, tag);
        },
        SET_ALL_EXISTING_AMIS_TAGS(state, value) {
            state.allExistingAMISDbids = value;
        },
        SET_FILTERED_INSPECTIONS(state, value) {
            state.filteredInspection = value;
        },
        SET_SHOW_POPULATE_AUTOMATIC_TAGS_POPUP(state, value) {
            state.showPopulateAutomaticTagsPopup = value;
        },
        SET_POPULATE_AUTOMATIC_TAGS_GROUP(state, value) {
            state.populateAutomaticTagsGroup = value;
        },
        SET_THEMED_DB_IDS(state, value) {
            state.themedDbIds = value;
        },
        SET_TAGS_ACTIVITIES(state, value) {
            state.tagsActivities = value;
        },
        SET_AMIS_SEARCH_TEXT(state, value) {
            state.amisSearchText = value;
        },
        SET_INSPECTION_IDS_FILTER(state, value) {
            state.inspectionIdsFilter = value;
        },
        SET_LAST_AMIS_ADVANCED_FETCH_DATE(state, value) {
            state.lastAMISAdvancedFetchDate = value;
        },
        SET_TAG_DYNAMIC_GRAPH(state, value) {
            state.tagDynamicGraphs = value;
        },
        SET_DOCUMENTS_PANEL_CALLBACK(state, value) {
            state.documentsPanelCallback = value;
        },
        SET_FILTERED_TAGS(state, value) {
            state.filteredTags = value;
        },
        SET_INSPECTION_TAG_TREE(state, value) {
            state.inspectionTagTree = value;
        },
        SET_SELECTED_INSPECTION_TREE_TAG(state, value) {
            state.selectedInspectionTreeTag = value;
        },
        SET_SHOW_TAG_MASTER_RESULT(state, value) {
            state.showTagMasterResult = value;
        },
        SET_HARVESTED_NODES_IDS(state, value) {
            state.harvestedNodesIds = value;
        },
        SET_HARVESTERS(state, value) {
            state.harvesters = value;
        },
        SET_GET_BASE_TAGS_TIMEOUT(state, value) {
            state.getBaseTagsTimeout = value;
        },
        SET_INSPECTION_MASTER_TYPE_OPTIONS(state, value) {
            state.inspectionMasterTypeOptions = value;
        },
        SET_IS_EQUIPMENT_MASTER_AVAILABLE(state, value) {
            state.isEquipmentMasterAvailable = value;
        },
        SET_IS_PIPELINE_MASTER_AVAILABLE(state, value) {
            state.isPipelineMasterAvailable = value;
        },
        SET_IS_STRUCTURAL_MASTER_AVAILABLE(state, value) {
            state.isStructuralMasterAvailable = value;
        },
        SET_IS_INSPECTION_TREE_OPTION(state, value) {
            state.isInspectionTreeOption = value;
        },
        SET_SELECTED_MASTER_TAG_ID(state, value) {
            state.selectedMasterTagId = value;
        },
        SET_TAG_FOCUS_CONTEXT_ACTIVE(state, value) {
            state.isTagFocusContextActive = value;
        },
        SET_DATE_FILTER_TYPE(state, value) {
            state.dateFilterType = value;
        },
        SET_DATE_FILTER_VALUE(state, value) {
            state.dateFilterValue = value;
        },
        SET_ALL_EXISTING_AUTOMATIC_TAGS(state, value) {
            state.allExistingAutomaticDbids = value;
        },
        SET_INSPECTION_FILTERS_OPTIONS_WITH_CHECK(state, value) {
            state.inspectionFiltersOptionsWithCheck = value;
            let filterName = state.inspectionFiltersOptionsWithCheck.find(o => o.isChecked).name;
            if (filterName)
                store.commit('SET_INSPECTION_SELECTED_FILTER', filterName);
        },
        SET_INSPECTION_SELECTED_FILTER(state, value) {
            state.InspectionSelectedFilter = value;

            if (state.inspectionFiltersOptionsWithCheck.find(o => o.name == value)) {
                state.inspectionFiltersOptionsWithCheck.forEach(o => o.isChecked = o.name == value);
            }

            store.dispatch('UPDATE_AVAILABLE_FILTERS_COLORS');
        },
        ADD_CUSTOM_FILTER(state, { property }) {
            if (!state.customFilters.find(f => f.property === property)) {
                state.customFilters.push({ property, values: [], searchText: '', dateFrom: null, dateTo: null, min: null, max: null });
            }
        },
        REMOVE_CUSTOM_FILTER(state, property) {
            state.customFilters = state.customFilters.filter(f => f.property !== property);
        },
        UPDATE_CUSTOM_FILTER(state, payload) {
            const filter = state.customFilters.find(f => f.property === payload.property);
            if (filter) {
                if (payload.values !== undefined) filter.values = payload.values;
                if (payload.searchText !== undefined) filter.searchText = payload.searchText;
                if (payload.dateFrom !== undefined) filter.dateFrom = payload.dateFrom;
                if (payload.dateTo !== undefined) filter.dateTo = payload.dateTo;
                if (payload.min !== undefined) filter.min = payload.min;
                if (payload.max !== undefined) filter.max = payload.max;
            }
        },
        CLEAR_CUSTOM_FILTERS(state) {
            state.customFilters = [];
        },
        SET_CUSTOM_FILTERS(state, filters) {
            state.customFilters = filters;
        },
        SET_SELECTED_CML_ID(state, cmlId) {
            state.selectedCmlId = cmlId;
        },
        // AVA-437 Tier 2 — highlight a specific TmlPoint row when navigating from the TML popup.
        SET_SELECTED_TML_POINT_ID(state, tmlPointId) {
            state.selectedTmlPointId = tmlPointId;
        },
        SET_ISOLATED_CML_IDS(state, cmlIds) {
            state.isolatedCmlIds = cmlIds || [];
        },
        ADD_ISOLATED_CML_ID(state, cmlId) {
            if (cmlId && !state.isolatedCmlIds.includes(cmlId)) {
                state.isolatedCmlIds = [...state.isolatedCmlIds, cmlId];
            }
        },
        REMOVE_ISOLATED_CML_ID(state, cmlId) {
            state.isolatedCmlIds = state.isolatedCmlIds.filter(id => id !== cmlId);
        },
        CLEAR_ISOLATED_CML_IDS(state) {
            state.isolatedCmlIds = [];
        },
        SET_CML_MARKER_OWNER(state, owner) {
            state.cmlMarkerOwner = owner;
        },
    },

    actions: {
        RESET_EDITING_TAG({ dispatch, commit, state }) {
            commit('SET_EDITING_TAG', null);
            commit('SET_EDITING_AMIS_TAG', null);
            commit('SET_ADDING_TAG_INFO', null);
        },
        ADD_TAG_TO_PREVIOUS_TAGS({ dispatch, commit, state }, { tag }) {
            state.previousTags = [...state.previousTags, tag];
        },
        POP_TAG_FROM_PREVIOUS_TAGS({ dispatch, commit, state }) {
            if (!state.previousTags.length) return;

            let tag = state.previousTags.pop();

            dispatch('OPEN_TAG', { tag: tag.clone() });
        },
        CLEAR_PREVIOUS_TAGS({ dispatch, commit, state }) {
            state.previousTags = [];
        },
        /**
         * Open a document in the ViewersSection Document tab
         * Supports two modes:
         * - { tagId } - Fetches P&ID document linked to the tag (for P&ID button in TagsRegister)
         * - { document } - Uses the provided document directly (for per-document action in AttachmentsList)
         * @param {Object} context - Vuex action context
         * @param {Object} payload - { tagId?: string, document?: Object }
         * @returns {Promise<{ success: boolean, document?: Object, error?: string }>}
         */
        async OPEN_DOCUMENT_IN_VIEWER({ commit, dispatch, rootState, rootGetters }, payload) {
            try {
                let document;

                if (payload.document) {
                    // Case 1: Document provided directly (from AttachmentsList per-document action)
                    // Construct direct blob URL with fresh SAS token
                    if (!payload.document.url) {
                        const documentId = getDocumentId(payload.document);
                        if (!documentId) {
                            return { success: false, error: 'Document ID not available' };
                        }

                        // Use shared utility for consistent URL construction
                        const blobUrl = await getDocumentBlobUrl(payload.document);

                        document = {
                            ...payload.document,
                            url: blobUrl,
                            referenceDocumentId: documentId  // Required for PDF viewer markup support
                        };
                    } else {
                        document = payload.document;
                    }
                } else if (payload.tagId) {
                    // Case 2: TagId provided (from P&ID button) - fetch P&ID for tag
                    const pIdTypeId = rootGetters.GET_DOCUMENT_TYPES?.find(t => t.name === 'P&IDs')?.id;
                    if (!pIdTypeId) {
                        return { success: false, error: 'P&ID document type not configured' };
                    }

                    const { items } = await GET_ALL_DOCUMENTS({
                        AssetId: rootState.assetId,
                        ForgeTagsIds: [payload.tagId],
                        DocumentTypeIds: [pIdTypeId],
                    });

                    if (!items?.length) {
                        return { success: false, error: 'No P&ID document found for this tag' };
                    }

                    document = items[0];
                    commit('setTagIdToHighlight', payload.tagId);
                } else {
                    return { success: false, error: 'No document or tagId provided' };
                }

                // Set active document and open viewer
                commit('SET_ACTIVE_DOCUMENT', document);
                commit('TAGS_REGISTER/SET_VIEWER_OPENED', true, { root: true });
                commit('TAGS_REGISTER/SET_VIEWER_TAB', 'Document', { root: true });

                return { success: true, document };
            } catch (err) {
                debugError('TAGS', 'OPEN_DOCUMENT_IN_VIEWER: Error', err);
                return { success: false, error: err.message };
            }
        },
        GLOBAL_SEARCH({ dispatch, commit, state }, { payload, type }) {
            return new Promise((resolve, reject) => {
                HttpService.post({
                    url: `/api/tags/${type}/Search`,
                    data: payload,
                    headers: facilityHeaders(),
                }).then(response => {
                    resolve(response.data);
                }).catch(error => {
                    debugError(error);
                    reject(error);
                });
            });
        },
        GLOBAL_SEARCH_DBIDS({ dispatch, commit, state }, { payload, type }) {
            return new Promise((resolve, reject) => {
                HttpService.post({
                    url: `/api/tags/${type}/SearchDbIds`,
                    data: payload,
                    headers: facilityHeaders(),
                }).then(response => {
                    resolve(response.data);
                }).catch(error => {
                    debugError(error);
                    reject(error);
                });
            });
        },
        FETCH_ADMIN_AUTOMATIC_TAGS({ dispatch, commit, state, getters }, { assetId, reload = true, index = 0, range = 25, searchText = '' }) {
            return new Promise((resolve, reject) => {
                let text = searchText ? btoa(searchText) : '';
                HttpService.get({
                    url: `api/equipments/fetchAdminAutomaticEquipments`,
                    params: {
                        assetId: assetId,
                        index: index,
                        range: range,
                        searchText64: text
                    }
                }).then(response => {
                    if (reload) commit("SET_AUTOMATIC_TAGS", []);
                    let tags = response.data.map(o => Object.assign(new ForgeEquipment(), o));
                    tags.forEach(o => o.parseContent());
                    commit("SET_AUTOMATIC_TAGS", getters['GET_AUTOMATIC_TAGS'].concat(tags));
                    resolve();
                }).catch(error => {
                    debugError(error);
                    reject(error);
                });
            })
        },
        async FETCH_ALL_ANOMALIES_TAGS_INFO({ commit }, {
            assetId,
            force = false,
            redraw = true,
            commitResult = true,
        }) {
            const scope = captureMutationScope(assetId);
            const { data } = await HttpService.get({
                url: 'api/tags/anomalies/getAllTagsMinimalInfo',
                ...FACILITY_REQUEST,
                params: { assetId: scope.facilityId },
            });
            const tags = shortCatalog(data, 'Anomaly');
            if (!isMutationScopeCurrent(scope)) throw new Error('Mutation scope changed during anomaly refresh');
            if (commitResult) commit('SET_ALL_EXISTING_ANOMALIES_TAGS', tags);
            if (redraw) store.state.forgeService.update3dIconsInAllViews();
            debugLog(`⚡ All anomalies tags fetched: ${tags.length}${force ? ' (fresh)' : ''}`);
            return tags;
        },
        async FETCH_ALL_STRUCTURAL_TAGS_INFO({ commit }, {
            assetId,
            force = false,
            redraw = true,
            commitResult = true,
        }) {
            const scope = captureMutationScope(assetId);
            const { data } = await HttpService.get({
                url: 'api/tags/structural/getAllTagsMinimalInfo',
                ...FACILITY_REQUEST,
                params: { assetId: scope.facilityId },
            });
            const tags = shortCatalog(data, 'Structural');
            if (!isMutationScopeCurrent(scope)) throw new Error('Mutation scope changed during structural refresh');
            if (commitResult) commit('SET_ALL_EXISTING_STRUCTURAL_TAGS', tags);
            if (redraw) store.state.forgeService.update3dIconsInAllViews();
            debugLog(`⚡ All structural tags fetched: ${tags.length}${force ? ' (fresh)' : ''}`);
            return tags;
        },
        async FETCH_ALL_PIPELINES_TAGS_INFO({ commit }, {
            assetId,
            force = false,
            redraw = true,
            commitResult = true,
        }) {
            const scope = captureMutationScope(assetId);
            const { data } = await HttpService.get({
                url: 'api/tags/pipeline/getAllTagsMinimalInfo',
                ...FACILITY_REQUEST,
                params: { assetId: scope.facilityId },
            });
            const tags = shortCatalog(data, 'Pipeline');
            if (!isMutationScopeCurrent(scope)) throw new Error('Mutation scope changed during pipeline refresh');
            if (commitResult) commit('SET_ALL_EXISTING_PIPELINES_TAGS', tags);
            if (redraw) store.state.forgeService.update3dIconsInAllViews();
            debugLog(`⚡ All pipelines tags fetched: ${tags.length}${force ? ' (fresh)' : ''}`);
            return tags;
        },
        async fetchAllAutomaticTagsDbids({ commit, state }, {
            assetId,
            callback,
            force = false,
            redraw = true,
            commitResult = true,
        }) {
            if (!force && state.allExistingAutomaticDbids.length > 0) {
                callback?.();
                return state.allExistingAutomaticDbids;
            }
            const scope = captureMutationScope(assetId);
            const { data } = await HttpService.get({
                url: 'api/equipments/getAllAutomaticTagsDbids',
                ...FACILITY_REQUEST,
                params: { assetId: scope.facilityId },
            });
            const tags = shortCatalog(data);
            if (!isMutationScopeCurrent(scope)) throw new Error('Mutation scope changed during equipment refresh');
            if (commitResult) commit('SET_ALL_EXISTING_AUTOMATIC_TAGS', tags);
            if (redraw) store.state.forgeService.update3dIconsInAllViews();
            debugLog(`⚡ All automatic tags fetched: ${tags.length}${force ? ' (fresh)' : ''}`);
            callback?.();
            return tags;
        },
        fetchAllAMISColors({ dispatch, commit, state }) {
            forgeFetch(`api/Mods/GetLifeColorOption`, opts => {
                state.InspectionRemainingLifeColorOptions = opts?.data
            })
            forgeFetch(`api/Mods/GetCorrosionColorOption`, opts => {
                state.InspectionCorrRateColorOptions = opts?.data
            })
            forgeFetch(`api/Mods/GetThickPlanColorOption`, opts => {
                state.InspectionThickPlanColorOptions = opts?.data
            })
        },
        PUSH_TAGS_TO_AUTOMATIC_TAGS({ dispatch, commit, state }, { tags }) {
            if (tags.length < 1) return;

            let acceptedTags = tags.reduce((collection, item) => {
                if (!state.automaticTags.find(t => t.id == item.id)) {
                    collection.push(item)
                }
                return collection;
            }, [])

            state.automaticTags = [...state.automaticTags, ...acceptedTags];

            // acceptedTags = acceptedTags.filter(t => t.children.length).map(t => t.children[0].dbId);
            // state.filteredAutomaticTagsIds = [...state.filteredAutomaticTagsIds, ...acceptedTags];
        },
        PUSH_TAGS_TO_STRUCTURAL_TAGS({ dispatch, commit, state }, { tags }) {
            if (tags.length < 1) return;

            let acceptedTags = tags.reduce((collection, item) => {
                if (!state.structuralTags.find(t => t.id == item.id)) {
                    collection.push(item)
                }
                return collection;
            }, [])

            state.structuralTags = [...state.structuralTags, ...acceptedTags];

            // acceptedTags = acceptedTags.filter(t => t.children.length).map(t => t.children[0].dbId);
            // state.filteredStructuralTagsIds = [...state.filteredStructuralTagsIds, ...acceptedTags];
        },
        PUSH_TAGS_TO_INSPECTIONS_TAGS({ dispatch, commit, state }, { tags }) {
            if (tags.length < 1) return;

            let acceptedTags = tags.reduce((collection, item) => {
                if (!store.state.inspections.find(t => t.id == item.id)) {
                    collection.push(item)
                }
                return collection;
            }, [])

            store.state.inspections = [...store.state.inspections, ...acceptedTags];
        },
        PUSH_TAGS_TO_PIPELINES_TAGS({ dispatch, commit, state }, { tags }) {
            if (tags.length < 1) return;

            let acceptedTags = tags.reduce((collection, item) => {
                if (!state.pipelinesTags.find(t => t.id == item.id)) {
                    collection.push(item)
                }
                return collection;
            }, [])

            state.pipelinesTags = [...state.pipelinesTags, ...acceptedTags];

            // acceptedTags = acceptedTags.filter(t => t.children.length).map(t => t.children[0].dbId);
            // state.filteredPipelineTagsIds = [...state.filteredPipelineTagsIds, ...acceptedTags];
        },
        getAutomaticTagTagById({ dispatch, commit, state }, id) {
            return new Promise((resolve, reject) => {
                try {
                    forgeFetch(`api/equipments/getAutomaticTagById?id=${id}&assetId=${router.currentRoute.params.id || ''}`, autoTag => {
                        if (autoTag) {
                            autoTag = Object.assign(new ForgeEquipment(), autoTag)
                            autoTag.parseContent()
                            resolve(autoTag);
                        }
                    })
                } catch (error) {
                    debugLog(error);
                    reject(error);
                }
            })
        },
        async fetchAllAMISTagsDbids({ dispatch, commit, state }, {
            assetId,
            fetched,
            callback,
            force = false,
            redraw = false,
            commitResult = true,
        }) {
            const scope = captureMutationScope(assetId);
            const { data } = await HttpService.get({
                url: 'api/AMIS/getAllAMISDbids',
                ...FACILITY_REQUEST,
                params: { assetId: scope.facilityId, fetched },
            });
            const allDbids = requireCatalogArray(data, 'Inspection');
            allDbids.forEach(o => {
                    o.point = o.point ? Object.assign(new THREE.Vector3(), o.point) : null;
                    o.type = 'Inspection';
            });
            if (!isMutationScopeCurrent(scope)) throw new Error('Mutation scope changed during inspection refresh');
            if (commitResult) commit('SET_ALL_EXISTING_AMIS_TAGS', allDbids);

            // MOCK DATA GENERATION - TODO: Remove when backend integration is complete
            if (commitResult) {
                dispatch('GENERATE_MOCK_FILTER_DATA');
                dispatch('UPDATE_AVAILABLE_FILTERS_COLORS');
            }
            if (redraw) store.state.forgeService.update3dIconsInAllViews();
            debugLog(`⚡ All inspection tags fetched: ${allDbids.length}${force ? ' (fresh)' : ''}`);
            callback?.();
            return allDbids;
        },
        
        // MOCK DATA GENERATION ACTION - TODO: Remove when backend returns real data
        // This action generates random coating condition and defect severity values for demonstration
        // Once backend API returns coatingCondition, defectSeverity, and zoneLinkId, remove this action
        GENERATE_MOCK_FILTER_DATA({ state }) {
            const coatingConditions = ['ri0', 'ri1', 'ri2', 'ri3', 'ri4', 'ri5', null];
            const defectSeverities = ['criticalDefect', 'priorityDefect', 'defect', 'minorImpairment', 'goodCondition', null];
            const zoneIds = [1, 2, 3, 4]; // Mock zone IDs
            
            state.allExistingAMISDbids.forEach(tag => {
                // Randomly assign coating condition (70% chance of having a value)
                if (!tag.coatingCondition) {
                    tag.coatingCondition = Math.random() > 0.3 
                        ? coatingConditions[Math.floor(Math.random() * (coatingConditions.length - 1))]
                        : null;
                }
                
                // Randomly assign defect severity (80% chance of having a value)
                if (!tag.defectSeverity) {
                    tag.defectSeverity = Math.random() > 0.2
                        ? defectSeverities[Math.floor(Math.random() * (defectSeverities.length - 1))]
                        : null;
                }
                
                // Randomly assign zone (90% chance of having a value)
                if (!tag.zoneLinkId) {
                    tag.zoneLinkId = Math.random() > 0.1
                        ? zoneIds[Math.floor(Math.random() * zoneIds.length)]
                        : null;
                }
            });
        },
        getInspectionsToCapture({ dispatch, commit, state }, { assetId, callback }) {
            forgeFetch(`api/forge/inspections/getInspectionsToCapture?assetId=${assetId}`, allDbids => {
                if (!allDbids) {
                    state.inspectionDbIdsToCapture = [];
                    if (callback)
                        callback();
                    return;
                }

                // dbid alias removed — consumers now use children.map(c => c.dbId) directly
                state.inspectionDbIdsToCapture = allDbids
                if (callback)
                    callback();
            })
        },
        GET_HARVESTER_TAGS_NEEDS_IMAGES_IDS({ dispatch, commit, state }, { harvestersIds, reset }) {
            return new Promise((resolve, reject) => {
                try {
                    HttpService.post({
                        url: `api/harvester/getTagsNeedsImagesIds`,
                        data: {
                            ids: harvestersIds,
                            reset: reset,
                            assetId: router.currentRoute.params.id || null
                        }
                    }).then(({ data }) => {
                        resolve(data);
                    }).catch(error => {
                        debugError(error);
                        reject(error);
                    });
                } catch (error) {
                    debugLog(error);
                    reject(error);
                }
            });
        },
        GET_AUTOMATIC_TAGS_NEED_IMAGES_IDS_BY_GROUP({ dispatch, commit, state }, { groupInfo, callback }) {
            let data = {
                'assetId': router.currentRoute.params.id,
                'harvesterId': groupInfo,
            }

            postDataAsync('api/equipments/getAutomaticTagsNeedsImagesIdsByGroup', data, res => {
                commit("SET_SELECTED_AUTOMATIC_TAGS_GROUP", res);
                if (callback) callback(res);
            });
        },
        LOAD_AMIS_COLORS({ dispatch, commit, state, getters }, callback) {
            // Set AMIS colors as default colors and fetch the asset color from the API.
            if (store.state.forgeSettings?.isAMIS || !store.state.forgeSettings?.inspectionFiltersColors) {
                store.state.forgeSettings.inspectionFiltersColors = (new ForgeSettings()).inspectionFiltersColors;
            }

            let colors = store.state.forgeSettings?.getInspectionFiltersColors() ?? [];


            state.InspectionCorrRateColorOptions.forEach(f => {
                let c = colors.find(o => o.code == f.code && o.symbol == f.judgSymb);
                if (c) {
                    f.judgCor = c.color;
                }
            });

            state.InspectionRemainingLifeColorOptions.forEach(f => {
                let c = colors.find(o => o.code == f.code && o.symbol == f.judgSymb);
                if (c) {
                    f.judgCor = c.color;
                }
            });

            state.InspectionThickPlanColorOptions.forEach(f => {
                let c = colors.find(o => o.code == f.code && o.symbol == f.judgSymb);
                if (c) {
                    f.judgCor = c.color;
                }
            });

            state.InspectionPlanColorOptions.forEach(f => {
                let c = colors.find(o => o.code == f.code && o.symbol == f.judgSymb);
                if (c) {
                    f.judgCor = c.color;
                }
            });

            dispatch('UPDATE_AVAILABLE_FILTERS_COLORS');

            // Sync display-layer state so UI reflects updated counts after custom filter changes.
            // updateInspectionsFilterColors() does this when inspection type changes,
            // but LOAD_AMIS_COLORS was missing it.
            state.InspectionsAvailableColorOptions = [...state.InspectionAvailableColorOptions];
            state.inspectionsSelectedColorOptions = [...state.selectedColorOptions];

            if (callback) callback();
        },
        LOAD_ASSET_UNITS({ dispatch, commit, state, getters }, callback) {
            var assetId = getters['getAssetId'];
            forgeFetchFacility(`api/forge/inspections/units/${assetId}`, units => {
                units = units.map(o => ({ id: o.pltCode, name: o.pltName, isChecked: false }));
                for (let i = 0; i < 2; i++) {
                    const unit = units[i];
                    if (unit) {
                        unit.isChecked = true;
                    }
                }
                commit('SET_AMIS_UNITS', units);
            })
        },
        LOAD_AMIS_UNITS({ dispatch, commit, state, getters }, callback) {
            var assetId = getters['getAssetId'];
            forgeFetchFacility(`api/AMIS/getAllAMISUnits?assetId=${assetId}`, units => {
                units = [...new Set(units)]
                units = units.map(o => ({ id: o.pltCode, name: o.pltName, isChecked: false }));
                for (let i = 0; i < 2; i++) {
                    const unit = units[i];
                    if (unit) {
                        unit.isChecked = true;
                    }
                }
                commit('SET_AMIS_UNITS', units);
            })
        },
        
        // NEW ACTION - Load zones for spatial filtering
        // TODO: Backend Integration - Replace mock data with GET /api/assets/{assetId}/zones
        LOAD_AVA_INSPECT_ZONES({ commit, getters }) {
            // MOCK DATA - Replace with real API call when backend is ready
            // const assetId = getters['getAssetId'];
            // HttpService.get({ url: `/api/assets/${assetId}/zones` }).then(response => {
            //     commit('SET_AVA_INSPECT_ZONES', response.data);
            // });
            
            // Mock zones data for demonstration
            const mockZones = [
                {
                    id: 1,
                    name: 'Zone A - Reactor Area',
                    code: 'ZONE-A',
                    color: '#2196F3',
                    description: 'Main reactor vessel and associated piping',
                    area: 150.5 // m²
                },
                {
                    id: 2,
                    name: 'Zone B - Heat Exchanger',
                    code: 'ZONE-B',
                    color: '#4CAF50',
                    description: 'Heat exchanger equipment zone',
                    area: 85.2 // m²
                },
                {
                    id: 3,
                    name: 'Zone C - Piping Network',
                    code: 'ZONE-C',
                    color: '#FF9800',
                    description: 'Main piping distribution network',
                    area: 220.8 // m²
                },
                {
                    id: 4,
                    name: 'Zone D - Storage Tanks',
                    code: 'ZONE-D',
                    color: '#9C27B0',
                    description: 'Storage tank farm',
                    area: 180.0 // m²
                }
            ];
            
            commit('SET_AVA_INSPECT_ZONES', mockZones);
        },
        UPDATE_AVAILABLE_FILTERS_COLORS({ dispatch, commit, state, getters }, callback) {
            debugLog(`⚡ Updating available filters colors...`);
            let res = [];
            let options = [];
            let targetProperty = '';
        
            const filter = state.InspectionSelectedFilter;
        
            const filterMappings = {
                'Remaining Thickness': { options: state.InspectionThickPlanColorOptions, property: 'rt' },
                'Remaining Life': { options: state.InspectionRemainingLifeColorOptions, property: 'rl' },
                'Corrosion Rate': { options: state.InspectionCorrRateColorOptions, property: 'cr' },
                'Inspection Plan': { options: state.InspectionPlanColorOptions, property: 'ispDateDays' },
                'AI Inspection': { options: state.AIInspectionColorOptions, property: 'severity' },
                // NEW FILTER MAPPINGS - Remote Condition Assessment
                'Coating Condition': { options: state.CoatingConditionColorOptions, property: 'coatingCondition' },
                'Corrosion Condition': { options: state.CorrosionConditionColorOptions, property: 'categoryId' },
                'Severity': { options: state.DefectSeverityColorOptions, property: 'defectSeverity' }
            };
        
            const isLastInspection = filter?.includes('Last Inspection');
            const isNextInspection = filter?.includes('Next Inspection');
            const isAIInspection = filter === 'AI Inspection';
        
            if (filterMappings[filter]) {
                ({ options, property: targetProperty } = filterMappings[filter]);
            } else if (isLastInspection || isNextInspection) {
                options = isLastInspection ? state.LastInspectionColorOptions : state.NextInspectionColorOptions;
                targetProperty = isLastInspection
                    ? (filter.includes('Thickness') ? 'ispDateDays' : filter.includes('Visual') ? 'offerLastInspDateDays' : 'nearestLastDateDays')
                    : (filter.includes('Thickness') ? 'nIspDateDays' : filter.includes('Visual') ? 'offerNextInspDateDays' : 'closestNextDateDays');
            }
        
            const allTagsInfo = state.allExistingAMISDbids ?? [];

            // Use backend-filtered IDs (prSearch + prFilter) as membership check
            // instead of re-implementing all filtering locally.
            const allFiltered = state.allAMISFilteredInspections || [];
            const filteredIdSet = allFiltered.length > 0
                ? new Set(allFiltered.map(t => t.id))
                : null; // null = no API response yet → include all tags

            // Zone filtering (frontend-only)
            const zones = state.avaInspectZones;
            const selectedZoneIds = zones.filter(z => z.isChecked).map(z => z.id);
            const zoneNeedCheck = zones.length > 0 && zones.some(z => !z.isChecked);

            const filteredTags = allTagsInfo.filter(e => {
                const matchesBackend = filteredIdSet === null || filteredIdSet.has(e.id);
                const matchesZone = !zoneNeedCheck || selectedZoneIds.includes(e.zoneLinkId);
                return matchesBackend && matchesZone;
            });
            
            options.forEach(option => {
                let tags = [];
                if (isAIInspection) {
                    tags = filteredTags.filter(e => e.anomalyInsightId &&
                        (e[targetProperty] == option.value || (e[targetProperty] == null && option.value == null))
                    );
                } else {
                    tags = filteredTags.filter(e => matchesBucket(e[targetProperty], option));
                }
            
                res.push({
                    id: `${option.code}-${option.codeOrdr}`,
                    title: option.title,
                    name1: option.judgMkr1 ? `${option.judgMkr1.includes('<=') ? '≤' : option.judgMkr1} ${option.judgCrr1}` : "N/A",
                    name2: option.judgMkr2 ? `${option.judgMkr2.includes('>=') ? '≥' : option.judgMkr2} ${option.judgCrr2}` : "N/A",
                    judgCor: option.judgCor,
                    judgCrr1: option.judgCrr1,
                    judgMkr1: option.judgMkr1,
                    judgCrr2: option.judgCrr2,
                    judgMkr2: option.judgMkr2,
                    judgSymb: option.judgSymb,
                    tags
                });
            });
                    
            const selectedIds = state.selectedColorOptions?.map(t => t.id) || [];
            state.InspectionAvailableColorOptions = res;
            state.selectedColorOptions = res.filter(i => selectedIds.includes(i.id));
        
            if (callback) callback(res);
        },
        OPEN_TAG({ dispatch, commit, state }, { tag, clearPrevious, defaultTab = null }) {
            // Set the one-time tab override (null means use default 'info'). Taken by the panel that
            // opens next, never by one already open on another tag. Untargeted: some entry paths pass
            // a sparse DTO, so the id the panel ends up showing is not guaranteed to be this one.
            commit('SET_TAG_INFO_DEFAULT_TAB', tagInfoTabRequest(defaultTab, { onOpen: true }));

            if (tag instanceof ForgeInspection) {
                commit('SET_EDITING_AMIS_TAG', tag);
                store.state.forgeService.showEditInspectionUI(tag, clearPrevious);

                // Some entry paths (Global Search baseTags/search row) pass a
                // sparse DTO that's missing fields like masterTagId. Detect
                // this by the absence of the masterTagId own-property and
                // fetch the full DTO in the background so downstream consumers
                // (Monitoring tab cascade, etc.) get a complete object.
                // Fire-and-forget: the panel opens immediately with the sparse
                // tag, then upgrades when the full DTO arrives. We swap only
                // if the user hasn't navigated to a different inspection in
                // the meantime.
                if (tag && tag.id && !('masterTagId' in tag)) {
                    dispatch('FETCH_INSPECTION_TAG_BY_ID', tag.id)
                        .then((hydrated) => {
                            if (state.editingAMISTag && state.editingAMISTag.id === tag.id) {
                                commit('SET_EDITING_AMIS_TAG', hydrated);
                                debugLog(`OPEN_TAG: hydrated sparse inspection ${tag.id} (masterTagId=${hydrated.masterTagId})`);
                            }
                        })
                        .catch((err) => {
                            debugError(`OPEN_TAG: hydration failed for inspection ${tag.id}`, err);
                        });
                }
            } else {
                commit('SET_EDITING_TAG', tag);
                store.state.forgeService.showEditTagUI(tag, clearPrevious);
            }
        },
        // With any change with units we need to update the unit list and the color filters.
        // Units changes on the inspections updates.
        UPDATE_INSPECTIONS_UNITS({ dispatch, commit, state, getters }) {
            let old = getters['GET_AMIS_UNITS'];

            let units = getters['getInspections'].filter(i => i.pltName).map(i => {
                return { id: i.pltName, name: i.pltName, isChecked: true };
            });

            const objSet = new Set();
            const uniqueUnits = [];

            for (const obj of units) {
                if (!objSet.has(obj.id)) {
                    // To save the old units check value.
                    let checked = old.find(u => u.name == obj.name)?.isChecked ?? true;
                    obj.isChecked = checked;
                    objSet.add(obj.id);
                    uniqueUnits.push(obj);
                }
            }

            commit('SET_AMIS_UNITS', uniqueUnits);

            dispatch('UPDATE_AVAILABLE_FILTERS_COLORS', () => {
                let ids = state.selectedColorOptions.map(t => t.id);
                state.selectedColorOptions = [...state.InspectionAvailableColorOptions.filter(i => ids.includes(i.id))];
            });
        },
        RESET_HARVESTER_INFO({ dispatch, commit, state, getters }) {
            commit('SET_TAG_HARVESTER_INFO', {
                id: null,
                title: "",
                filterOptions: [],
                markIgnore: false,
                removeSlash: false,
                selectedTreeLevel: "All Tiers",
                matchingCategory: "",
                matchingProperty: ""
            });
        },
        async VALIDATE_IGNORED_NODE_IDS({ dispatch, commit, state, getters }, { nodesIds }) {
            debugLog(`🔎 Validate ignored nodes (${nodesIds.length} nodes)`);

            const BATCH_SIZE = API_LIMITS.MAX_MODEL_NODES;
            const assetId = router.currentRoute.params.id;
            let allIgnoredIds = [];

            try {
                // Process in batches of 500 to stay within API limit
                for (let i = 0; i < nodesIds.length; i += BATCH_SIZE) {
                    const batch = nodesIds.slice(i, i + BATCH_SIZE);
                    debugLog(`🔄 Validating ignored batch ${Math.floor(i / BATCH_SIZE) + 1}/${Math.ceil(nodesIds.length / BATCH_SIZE)} (${batch.length} nodes)`);

                    const { data } = await HttpService.post({
                        url: 'api/equipment/validateIgnoredIds',
                        data: {
                            "nodesIds": batch,
                            "assetId": assetId
                        },
                    });

                    if (data) {
                        allIgnoredIds = allIgnoredIds.concat(data);
                    }
                }

                debugLog(`✅ Validate ignored nodes completed (${allIgnoredIds.length} ignored)`);
                return allIgnoredIds;
            } catch (error) {
                debugError("❌ Validate ignored nodes: ", error);
                throw error;
            }
        },
        async VALIDATE_DUPLICATED_NODE_IDS({ dispatch, commit, state, getters }, { nodesIds }) {
            debugLog(`🔎 Validate duplicated nodes (${nodesIds.length} nodes)`);

            const BATCH_SIZE = API_LIMITS.MAX_MODEL_NODES;
            const assetId = router.currentRoute.params.id;
            let allDuplicatedIds = [];

            try {
                // Process in batches of 500 to stay within API limit
                for (let i = 0; i < nodesIds.length; i += BATCH_SIZE) {
                    const batch = nodesIds.slice(i, i + BATCH_SIZE);
                    debugLog(`🔄 Validating duplicated batch ${Math.floor(i / BATCH_SIZE) + 1}/${Math.ceil(nodesIds.length / BATCH_SIZE)} (${batch.length} nodes)`);

                    const { data } = await HttpService.post({
                        url: 'api/equipment/validateDuplicatedIds',
                        data: {
                            "nodesIds": batch,
                            "assetId": assetId
                        },
                    });

                    if (data) {
                        allDuplicatedIds = allDuplicatedIds.concat(data);
                    }
                }

                debugLog(`✅ Validate duplicated nodes completed (${allDuplicatedIds.length} duplicated)`);
                return allDuplicatedIds;
            } catch (error) {
                debugError("❌ Validate duplicated nodes: ", error);
                throw error;
            }
        },
        GET_MASTERS_STATUS({ dispatch, commit, state, getters }) {
            return new Promise((resolve, reject) => {
                let assetId = router.currentRoute.params.id;

                HttpService.get({
                    url: `api/harvester/getMastersStatus/${assetId}`,
                }).then(({ data }) => {
                    debugLog(`✅ Get masters status`);
                    commit('SET_IS_EQUIPMENT_MASTER_AVAILABLE', !!data?.isEquipmentMasterAvailable);
                    commit('SET_IS_PIPELINE_MASTER_AVAILABLE', !!data?.isPipelineMasterAvailable);
                    commit('SET_IS_STRUCTURAL_MASTER_AVAILABLE', !!data?.isStructuralMasterAvailable);
                    resolve(data);
                }).catch(error => {
                    debugError("❌ Get masters status: ", error);
                    reject(error);
                });
            })
        },
        UPDATE_HARVESTED_NODES_IDS({ dispatch, commit, state, getters }) {
            debugLog(`🔎 Update harvested nodes`);

            return new Promise((resolve, reject) => {
                HttpService.get({
                    url: 'api/tags/getHarvestedDbIds',
                    params: {
                        "assetId": router.currentRoute.params.id
                    },
                }).then(({ data }) => {
                    debugLog(`✅ Update harvested nodes`);

                    commit('SET_HARVESTED_NODES_IDS', new Uint32Array(data));
                    resolve(data);
                }).catch(error => {
                    debugError("❌ Update harvested nodes: ", error);
                    reject(error);
                });
            })
        },
        FETCH_ADMIN_HARVESTERS({ dispatch, commit, state, getters }) {
            HttpService.get({
                url: 'api/harvester/getAssetHarvesters',
                params: {
                    "assetId": router.currentRoute.params.id
                },
            }).then(({ data }) => {
                commit('SET_HARVESTERS', data ?? []);
            })
        },
        /**
         * Concurrent callers asking for the SAME tag in the same facility share one request.
         *
         * The More menu resolves two intents for one tag (auto-zoom and isolate-3d) and each used
         * to fetch for itself: two identical GETs side by side in the network log, and two plans
         * built from two separate reads of the row. De-duplicating here rather than in that caller
         * fixes it wherever it happens and keeps every call site's code unchanged.
         *
         * Strictly in-flight - the entry is dropped the moment the request settles - so this is a
         * request collapse, never a cache. Nobody can read a stale tag from it.
         */
        GET_BASE_TAG_BY_ID({ dispatch, commit, state, getters }, request) {
            const isScopedRequest = request != null && typeof request === 'object' && !Array.isArray(request);
            const id = isScopedRequest ? request.id : request;
            const assetId = isScopedRequest && Object.prototype.hasOwnProperty.call(request, 'assetId')
                ? request.assetId
                : router.currentRoute.params.id || null;
            const expectedKind = isScopedRequest ? request.expectedKind : null;
            const inFlightKey = `${id}|${assetId}|${expectedKind ?? ''}`;
            const alreadyInFlight = baseTagReadsInFlight.get(inFlightKey);
            if (alreadyInFlight) return alreadyInFlight;
            const read = readBaseTagFresh(request);
            baseTagReadsInFlight.set(inFlightKey, read);
            // Settled is settled, success or failure: the next caller must issue a fresh request.
            return read.finally(() => baseTagReadsInFlight.delete(inFlightKey));
        },
        GET_BASE_TAG_BY_ID_FRESH(_, request) {
            return readBaseTagFresh(request);
        },
        async NAVIGATE_TAG_SPATIALLY({ dispatch, rootState }, payload = {}) {
            const isResolveOnly = payload.resolveOnly === true || payload.intent === 'copy-link';
            const requestId = isResolveOnly ? null : ++latestSpatialNavigationRequestId;
            const assetId = payload.assetId
                ?? rootState?.assetId
                ?? store.state.assetId
                ?? null;
            const selectedFacilityId = payload.selectedFacilityId ?? null;
            let plan = null;
            const isFacilityCurrent = () => String(
                rootState?.assetId ?? store.state.assetId ?? '',
            ).toLowerCase() === String(assetId ?? '').toLowerCase();
            const isRequestCurrent = () => isFacilityCurrent()
                && (requestId === null || requestId === latestSpatialNavigationRequestId);

            try {
                const tag = await dispatch('GET_BASE_TAG_BY_ID', {
                    id: payload.tagId,
                    assetId,
                    expectedKind: null,
                });

                if (!isRequestCurrent()) {
                    return { status: 'stale', reason: 'superseded-intent', plan: null };
                }

                const isLegacyLinkedTarget = !Object.prototype.hasOwnProperty.call(
                    tag || {},
                    'spatialTargets',
                ) && !!tag?.linked360ImageInfo?.imageId;
                const intentCanResolve360 = [
                    TAG_SPATIAL_INTENTS.AUTO_ZOOM,
                    TAG_SPATIAL_INTENTS.COPY_LINK,
                    TAG_SPATIAL_INTENTS.EXPLICIT_360,
                    TAG_SPATIAL_INTENTS.TASK_FOCUS,
                ].includes(payload.intent);
                if (isLegacyLinkedTarget && intentCanResolve360
                    && !(rootState?.projects || []).length) {
                    await dispatch('LOAD_FACILITY_PROJECTS', assetId);
                    if (!isRequestCurrent()) {
                        return { status: 'stale', reason: 'superseded-intent', plan: null };
                    }
                }

                plan = resolveTagSpatialIntent(tag, {
                    currentFacilityId: assetId,
                    intent: payload.intent,
                    selectedFacilityId,
                    // Opt-in, default off: only a caller that can also frame a bare point
                    // (the tag card's Isolate) may resolve a 360 placement to its 3D point.
                    // Every other dispatcher sends isolate=true and would land on the
                    // element-isolate path with nothing to isolate.
                    allowPointOnly3d: payload.allowPointOnly3d === true,
                    currentFacilityImageIds: (rootState?.projects || [])
                        .flatMap(project => project.images || [])
                        .map(image => image.id),
                });
                if (isResolveOnly) {
                    return {
                        status: plan.kind === 'choose-facility' ? 'choose-facility' : 'resolved',
                        reason: plan.reason,
                        plan,
                    };
                }
                if (plan.kind === 'choose-facility') {
                    return { status: 'choose-facility', reason: plan.reason, plan };
                }

                const execution = await executeTagSpatialIntent({
                    plan,
                    tag,
                    isolate: payload.isolate === true,
                    source: payload.source || 'ui',
                    requestedIntent: payload.intent,
                    intentId: requestId,
                    dependencies: {
                        isLatestIntent: id => isFacilityCurrent()
                            && id === latestSpatialNavigationRequestId,
                        async loadDestinationFacility(facilityId) {
                            const query = plan.viewer === '360'
                                ? { tagId: tag.id, zoom360: 'true' }
                                : { tagId: tag.id, isolate: 'true' };
                            const route = router.resolve({
                                name: 'facility',
                                params: { id: facilityId },
                                query,
                            });
                            window.open(route.href, '_blank', 'noopener,noreferrer');
                        },
                    },
                });
                return { ...execution, plan };
            } catch (err) {
                if (!isRequestCurrent()) {
                    return { status: 'stale', reason: 'superseded-intent', plan };
                }
                debugError('❌ Spatial navigation failed: ', err);
                return { status: 'failed', reason: 'spatial-navigation-failed', plan };
            }
        },
        TIME_OUT_GET_BASE_TAGS_BY_IDS({ dispatch, commit, state, getters }, ids) {
            return new Promise((resolve, reject) => {
                let lastTimeout = getters.GET_GET_BASE_TAGS_TIMEOUT;

                if (lastTimeout) clearTimeout(lastTimeout);

                let timeout = setTimeout(() => {
                    resolve(dispatch('GET_BASE_TAGS_BY_IDS', ids));
                }, 200);

                commit('SET_GET_BASE_TAGS_TIMEOUT', timeout);
            });
        },
        GET_BASE_TAGS_INFO_BY_IDS({ dispatch, commit, state, getters }, ids) {
            return new Promise((resolve, reject) => {
                HttpService.post({
                    url: 'api/tags/getBaseTagsByIds',
                    data: {
                        ids: ids,
                        assetId: router.currentRoute.params.id || null
                    }
                }).then(({ data }) => {
                    let tags = []

                    for (let i = 0; i < data.length; i++) {
                        let tag = data[i];
                        tags.push(ForgeBaseTag.getTagObject(tag));
                    }

                    resolve(tags ?? []);
                }).catch(error => {
                    reject(error);
                });
            });
        },
        GET_BASE_TAG_BY_ID_NAME({ dispatch, commit, state, getters }, { id, name }) {
            return new Promise((resolve, reject) => {
                HttpService.get({
                    url: 'api/tags/getBaseTagByIdName',
                    params: {
                        id: id,
                        name: name,
                        assetId: router.currentRoute.params.id || null
                    }
                }).then(({ data }) => {
                    if (!data) return reject();
                    let tag = ForgeBaseTag.getTagObject(data);
                    resolve(tag);
                }).catch(error => {
                    debugError("❌ Get base tag by id name: ", error);
                    reject(error);
                });
            })
        },
        GET_CML_RECORD_BY_ID({ dispatch, commit, state, getters },  id ) {
            return new Promise((resolve, reject) => {
                HttpService.get({
                    url: `api/tags/cml-record/${id}`,
                    
                }).then(({ data }) => {
                    if (!data) return reject();
                    let record=Object.assign(new CmlRecord(),data);
                   record.parseContent();
                    resolve(record);
                }).catch(error => {
                    debugError("❌ Get base tag by id name: ", error);
                    reject(error);
                });
            })
        },
        GET_BASE_TAGS_BY_IDS({ dispatch, commit, state, getters }, ids) {
            return new Promise((resolve, reject) => {
                HttpService.post({
                    url: 'api/tags/getBaseTagsByIds',
                    data: {
                        ids: ids,
                        assetId: router.currentRoute.params.id || null
                    }
                }).then(({ data }) => {
                    if (!data) return resolve([]);

                    let result = [];
                    let currentTag = null;

                    data.forEach(tag => {
                        switch (tag.discriminator) {
                            case 'ForgeAnomaly':
                                currentTag = Object.assign(new ForgeAnomaly(), tag);
                                break;
                            case 'ForgeEquipment':
                                currentTag = Object.assign(new ForgeEquipment(), tag);
                                break;
                            case 'ForgeStructural':
                                currentTag = Object.assign(new ForgeStructural(), tag);
                                break;
                            case 'ForgePipeline':
                                currentTag = Object.assign(new ForgePipeline(), tag);
                                break;
                            case 'ForgeScaffold':
                                currentTag = Object.assign(new ForgeScaffold(), tag);
                                break;
                            case 'ForgeAnnotation':
                                currentTag = Object.assign(new ForgeAnnotation(), tag);
                                break;
                            case 'ForgeInspection':
                                currentTag = Object.assign(new ForgeInspection(), tag);
                                break;
                            case 'ForgeAvaInspect':
                                currentTag = Object.assign(new ForgeAvaInspect(), tag);
                                break;
                            default:
                        }

                        if (currentTag) {
                            currentTag.parseContent();
                            result.push(currentTag);
                        }
                    });
                    resolve(result);
                }).catch(error => {
                    reject(error);
                });
            });
        },
        FETCH_TAG_BY_TYPE_AND_ID({ dispatch, commit, state, getters }, { type, id }) {
            debugLog(`🔎 Fetch tag by type and id: ${type} - ${id}`);

            // Capitalize the type
            type = type.charAt(0).toUpperCase() + type.slice(1);

            return new Promise((resolve, reject) => {
                switch (type) {
                    case 'General':
                    case 'Annotation':
                    case 'ForgeAnnotation':
                        dispatch('FETCH_GENERAL_TAG_BY_ID', id).then(resolve).catch(reject);
                        break;
                    case 'Anomaly':
                    case 'ForgeAnomaly':
                        dispatch('FETCH_ANOMALY_TAG_BY_ID', id).then(resolve).catch(reject);
                        break;
                    case 'Scaffold':
                    case 'scaffold':
                    case 'container':
                    case 'Container':
                    case 'permit':
                    case 'Permit':
                    case 'ForgeScaffold':
                        dispatch('FETCH_SCAFFOLD_TAG_BY_ID', id).then(resolve).catch(reject);
                        break;
                    case 'Equipment':
                    case 'ForgeEquipment':
                        dispatch('FETCH_EQUIPMENT_TAG_BY_ID', id).then(resolve).catch(reject);
                        break;
                    case 'Structural':
                    case 'ForgeStructural':
                        dispatch('FETCH_STRUCTURAL_TAG_BY_ID', id).then(resolve).catch(reject);
                        break;
                    case 'Pipeline':
                    case 'ForgePipeline':
                        dispatch('FETCH_PIPELINE_TAG_BY_ID', id).then(resolve).catch(reject);
                        break;
                    case 'Inspection':
                    case 'ForgeInspection':
                        dispatch('FETCH_INSPECTION_TAG_BY_ID', id).then(resolve).catch(reject);
                        break;
                    case 'AvaInspect':
                    case 'ForgeAvaInspect':
                        dispatch('FETCH_AVA_INSPECT_TAG_BY_ID', id).then(resolve).catch(reject);
                        break;
                    default:
                        reject(`❌ Fetch tag by type and id: Unknown type: ${type}`);
                }
            });
        },
        FETCH_GENERAL_TAG_BY_ID({ dispatch, commit, state, getters }, id) {
            return new Promise((resolve, reject) => {
                HttpService.get({
                    url: `api/tags/annotation/${id}`,
                    headers: facilityHeaders(),
                }).then(({ data }) => {
                    let tag = Object.assign(new ForgeAnnotation(), data);
                    tag.parseContent();
                    resolve(tag);
                }).catch(error => {
                    debugError("❌ Fetch general tag by id: ", error);
                    reject(error);
                });
            });
        },
        FETCH_ANOMALY_TAG_BY_ID({ dispatch, commit, state, getters, rootState }, id) {
            // Every anomaly fetch (3D icon and 360 hotspot clicks, search, popups) funnels through
            // here. Without assetId the server resolves with no facility, so spatialTargets lose
            // the placement scan and Zoom falls back to the model (AVA-492 Slice 2).
            const assetId = rootState.assetId;
            const assetQuery = assetId ? `?assetId=${encodeURIComponent(assetId)}` : '';
            return new Promise((resolve, reject) => {
                HttpService.get({
                    url: `api/tags/anomalies/${id}${assetQuery}`,
                }).then(({ data }) => {
                    let tag = Object.assign(new ForgeAnomaly(), data);
                    tag.parseContent();
                    resolve(tag);
                }).catch(error => {
                    debugError("❌ Fetch anomaly tag by id: ", error);
                    reject(error);
                });
            });
        },
        FETCH_SCAFFOLD_TAG_BY_ID({ dispatch, commit, state, getters, rootState }, id) {
            // assetId is optional server-side and additive: with it, a group-owned scaffold-family tag
            // is resolved to THIS facility's placement (TagFacilityOverride); without it the legacy
            // facility-less shape is returned (AVA-492 Phase 2).
            const assetId = rootState.assetId;
            const assetQuery = assetId ? `?assetId=${encodeURIComponent(assetId)}` : '';
            return new Promise((resolve, reject) => {
                HttpService.get({
                    url: `api/tags/scaffold/${id}${assetQuery}`,
                }).then(({ data }) => {
                    let tag = Object.assign(new ForgeScaffold(), data);
                    tag.parseContent();
                    resolve(tag);
                }).catch(error => {
                    debugError("❌ Fetch scaffold tag by id: ", error);
                    reject(error);
                });
            });
        },
        FETCH_STRUCTURAL_TAG_BY_ID({ dispatch, commit, state, getters }, id) {
            return new Promise((resolve, reject) => {
                HttpService.get({
                    url: `api/tags/structural/${id}`,
                    params: { assetId: router.currentRoute.params.id || null }
                }).then(({ data }) => {
                    let tag = Object.assign(new ForgeStructural(), data);
                    tag.parseContent();
                    store.dispatch('PUSH_TAGS_TO_STRUCTURAL_TAGS', { tags: [tag] });
                    resolve(tag);
                }).catch(error => {
                    debugError("❌ Fetch structural tag by id: ", error);
                    reject(error);
                });
            })
        },
        FETCH_PIPELINE_TAG_BY_ID({ dispatch, commit, state, getters }, id) {
            return new Promise((resolve, reject) => {
                HttpService.get({
                    url: `api/tags/pipeline/${id}`,
                    params: { assetId: router.currentRoute.params.id || null }
                }).then(({ data }) => {
                    let tag = Object.assign(new ForgePipeline(), data);
                    tag.parseContent();
                    store.dispatch('PUSH_TAGS_TO_PIPELINES_TAGS', { tags: [tag] });
                    resolve(tag);
                }).catch(error => {
                    debugError("❌ Fetch pipeline tag by id: ", error);
                    reject(error);
                });
            })
        },
        FETCH_INSPECTION_TAG_BY_ID({ dispatch, commit, state, getters }, id) {
            return new Promise((resolve, reject) => {
                HttpService.get({
                    url: `api/tags/inspections/${id}`,
                }).then(({ data }) => {
                    let tag = Object.assign(new ForgeInspection(), data);
                    tag.parseContent();
                    resolve(tag);
                }).catch(error => {
                    debugError("❌ Fetch inspection tag by id: ", error);
                    reject(error);
                });
            });
        },
        FETCH_EQUIPMENT_TAG_BY_ID({ dispatch, commit, state, getters }, id) {
            return new Promise((resolve, reject) => {
                HttpService.get({
                    url: `api/tags/equipment/${id}`,
                    params: { assetId: router.currentRoute.params.id || null }
                }).then(({ data }) => {
                    let tag = Object.assign(new ForgeEquipment(), data);
                    tag.parseContent();
                    store.dispatch('PUSH_TAGS_TO_AUTOMATIC_TAGS', { tags: [tag] });
                    resolve(tag);
                }).catch(error => {
                    debugError("❌ Fetch equipment tag by id: ", error);
                    reject(error);
                });
            })
        },
        GET_ANOMALIES_TAGS_BY_IDS({ dispatch, commit, state, getters }, { ids }) {
            return new Promise((resolve, reject) => {
                HttpService.post({
                    url: 'api/tags/anomalies/getTagsByIds',
                    data: {
                        "ids": ids,
                        "assetId": router.currentRoute.params.id
                    }
                }).then(({ data }) => {
                    let tags = data.map(o => Object.assign(new ForgeAnomaly(), o));
                    tags.forEach(o => o.parseContent());
                    resolve(tags);
                }).catch(error => {
                    debugError("❌ Fetch anomaly tags by ids: ", error);
                    reject(error);
                });
            })
        },
        GET_EQUIPMENT_TAGS_BY_IDS({ dispatch, commit, state, getters }, { ids }) {
            return new Promise((resolve, reject) => {
                HttpService.post({
                    url: 'api/tags/equipment/getTagsByIds',
                    data: {
                        "ids": ids,
                        "assetId": router.currentRoute.params.id
                    }
                }).then(({ data }) => {
                    let tags = data.map(o => Object.assign(new ForgeEquipment(), o));
                    tags.forEach(o => o.parseContent());
                    resolve(tags);
                }).catch(error => {
                    debugError("❌ Fetch equipment tags by ids: ", error);
                    reject(error);
                });
            })
        },
        GET_STRUCTURAL_TAGS_BY_IDS({ dispatch, commit, state, getters }, { ids }) {
            return new Promise((resolve, reject) => {
                HttpService.post({
                    url: 'api/tags/structural/getTagsByIds',
                    data: {
                        "ids": ids,
                        "assetId": router.currentRoute.params.id
                    }
                }).then(({ data }) => {
                    let tags = data.map(o => Object.assign(new ForgeStructural(), o));
                    tags.forEach(o => o.parseContent());
                    resolve(tags);
                }).catch(error => {
                    debugError("❌ Fetch structural tags by ids: ", error);
                    reject(error);
                });
            })
        },
        GET_PIPELINES_TAGS_BY_IDS({ dispatch, commit, state, getters }, { ids }) {
            return new Promise((resolve, reject) => {
                HttpService.post({
                    url: 'api/tags/pipeline/getTagsByIds',
                    data: {
                        "ids": ids,
                        "assetId": router.currentRoute.params.id
                    }
                }).then(({ data }) => {
                    let tags = data.map(o => Object.assign(new ForgePipeline(), o));
                    tags.forEach(o => o.parseContent());
                    resolve(tags);
                }).catch(error => {
                    debugError("❌ Fetch pipeline tags by ids: ", error);
                    reject(error);
                });
            })
        },
        async DELETE_ANOMALY_TAGS({ dispatch, commit }, { ids }) {
            const requestedIds = uniqueTagIds(ids);
            const scope = captureMutationScope();
            await HttpService.delete({
                url: 'api/tags/anomalies',
                data: { ids: requestedIds, assetId: scope.facilityId },
            });
            const geometryAuthorities = captureTagGeometryAuthorities(requestedIds, scope.facilityId);
            try {
                const [manualResponse, minimal] = await Promise.all([
                    HttpService.get({
                        url: 'api/tags/anomalies/getAllManualAnomalies',
                        ...FACILITY_REQUEST,
                        params: { assetId: scope.facilityId },
                    }),
                    dispatch('FETCH_ALL_ANOMALIES_TAGS_INFO', {
                        assetId: scope.facilityId, force: true, redraw: false, commitResult: false,
                    }),
                ]);
                const manual = hydrateCatalog(manualResponse.data, ForgeAnomaly, 'Manual anomaly');
                if (!isMutationScopeCurrent(scope)) return deleteReceipts(requestedIds, scope.facilityId);
                const catalog = [...manual, ...minimal];
                commit('SET_ALL_EXISTING_ANOMALIES_TAGS', minimal);
                recordCatalogTagGeometry(catalog, scope, geometryAuthorities);
                return deleteReceipts(requestedIds, scope.facilityId, catalog, true);
            } catch (err) {
                debugError('❌ Anomaly delete refresh failed:', err);
                return deleteReceipts(requestedIds, scope.facilityId);
            }
        },
        async DELETE_EQUIPMENT_TAGS({ dispatch, state, commit }, { ids }) {
            const requestedIds = uniqueTagIds(ids);
            const scope = captureMutationScope();
            const manualBefore = new Set((store.state.tags || [])
                .filter(tag => requestedIds.includes(String(tag?.id)))
                .map(tag => String(tag.id)));
            const automaticBefore = new Set([
                ...(state.allExistingAutomaticDbids || []),
                ...(state.automaticTags || []),
            ].filter(tag => requestedIds.includes(String(tag?.id))).map(tag => String(tag.id)));
            await HttpService.post({
                url: 'api/equipments/deleteEquipment',
                data: { tagsGuid: requestedIds, assetId: scope.facilityId },
            });
            const geometryAuthorities = captureTagGeometryAuthorities(requestedIds, scope.facilityId);

            const [manualResult, automaticResult] = await Promise.allSettled([
                HttpService.get({
                    url: 'api/equipments/getAllNormalEquipment',
                    ...FACILITY_REQUEST,
                    params: { assetId: scope.facilityId },
                }),
                dispatch('fetchAllAutomaticTagsDbids', {
                    assetId: scope.facilityId, force: true, redraw: false, commitResult: false,
                }),
            ]);
            if (!isMutationScopeCurrent(scope)) return deleteReceipts(requestedIds, scope.facilityId);

            let manual = [];
            let manualAuthoritative = false;
            if (manualResult.status === 'fulfilled') {
                try {
                    manual = hydrateCatalog(manualResult.value.data, ForgeEquipment, 'Manual equipment');
                    commit('setTags', manual);
                    manualAuthoritative = true;
                } catch (err) {
                    debugError('❌ Manual equipment refresh was malformed:', err);
                }
            }
            const automatic = automaticResult.status === 'fulfilled' ? automaticResult.value : [];
            if (automatic.length > 0) commit('SET_ALL_EXISTING_AUTOMATIC_TAGS', automatic);
            if (manualAuthoritative && automatic.length > 0) {
                const catalog = [...manual, ...automatic];
                recordCatalogTagGeometry(catalog, scope, geometryAuthorities);
                return deleteReceipts(requestedIds, scope.facilityId, catalog, true);
            }
            recordCatalogTagGeometry([...manual, ...automatic], scope, geometryAuthorities);
            const manualReceipts = new Map(deleteReceipts(
                [...manualBefore], scope.facilityId, manual, manualAuthoritative,
            ).map(receipt => [receipt.tagId, receipt]));
            const automaticAuthoritative = automaticResult.status === 'fulfilled' && automatic.length > 0;
            const automaticReceipts = new Map(deleteReceipts(
                [...automaticBefore], scope.facilityId, automatic, automaticAuthoritative,
            ).map(receipt => [receipt.tagId, receipt]));
            return requestedIds.map(id => manualReceipts.get(id)
                || automaticReceipts.get(id)
                || deleteReceipts([id], scope.facilityId)[0]);
        },
        async DELETE_PIPELINE_TAGS({ dispatch, commit }, { ids }) {
            const requestedIds = uniqueTagIds(ids);
            const scope = captureMutationScope();
            await HttpService.post({
                url: 'api/tags/pipeline/deletePipelines',
                data: { ids: requestedIds, assetId: scope.facilityId },
            });
            const geometryAuthorities = captureTagGeometryAuthorities(requestedIds, scope.facilityId);
            try {
                const [manualResponse, minimal] = await Promise.all([
                    HttpService.get({
                        url: 'api/tags/pipeline/getAllManualPipelines',
                        ...FACILITY_REQUEST,
                        params: { assetId: scope.facilityId },
                    }),
                    dispatch('FETCH_ALL_PIPELINES_TAGS_INFO', {
                        assetId: scope.facilityId, force: true, redraw: false, commitResult: false,
                    }),
                ]);
                const manual = hydrateCatalog(manualResponse.data, ForgePipeline, 'Manual pipeline');
                if (!isMutationScopeCurrent(scope)) return deleteReceipts(requestedIds, scope.facilityId);
                commit('SET_MANUAL_PIPELINES_TAGS', manual);
                commit('SET_ALL_EXISTING_PIPELINES_TAGS', minimal);
                const catalog = [...manual, ...minimal];
                recordCatalogTagGeometry(catalog, scope, geometryAuthorities);
                return deleteReceipts(requestedIds, scope.facilityId, catalog, true);
            } catch (err) {
                debugError('❌ Pipeline delete refresh failed:', err);
                return deleteReceipts(requestedIds, scope.facilityId);
            }
        },
        async DELETE_STRUCTURAL_TAGS({ dispatch, commit }, { ids }) {
            const requestedIds = uniqueTagIds(ids);
            const scope = captureMutationScope();
            await HttpService.post({
                url: 'api/tags/structural/deleteStructural',
                data: { ids: requestedIds, assetId: scope.facilityId },
            });
            const geometryAuthorities = captureTagGeometryAuthorities(requestedIds, scope.facilityId);
            try {
                const [manualResponse, minimal] = await Promise.all([
                    HttpService.get({
                        url: 'api/tags/structural/getAllManualStructural',
                        ...FACILITY_REQUEST,
                        params: { assetId: scope.facilityId },
                    }),
                    dispatch('FETCH_ALL_STRUCTURAL_TAGS_INFO', {
                        assetId: scope.facilityId, force: true, redraw: false, commitResult: false,
                    }),
                ]);
                const manual = hydrateCatalog(manualResponse.data, ForgeStructural, 'Manual structural');
                if (!isMutationScopeCurrent(scope)) return deleteReceipts(requestedIds, scope.facilityId);
                commit('SET_MANUAL_STRUCTURAL_TAGS', manual);
                commit('SET_ALL_EXISTING_STRUCTURAL_TAGS', minimal);
                const catalog = [...manual, ...minimal];
                recordCatalogTagGeometry(catalog, scope, geometryAuthorities);
                return deleteReceipts(requestedIds, scope.facilityId, catalog, true);
            } catch (err) {
                debugError('❌ Structural delete refresh failed:', err);
                return deleteReceipts(requestedIds, scope.facilityId);
            }
        },
        async DELETE_SCAFFOLDS_TAGS({ commit }, { ids }) {
            const requestedIds = uniqueTagIds(ids);
            const scope = captureMutationScope();
            const storedById = new Map((store.state.scaffolds || [])
                .filter(tag => requestedIds.includes(String(tag?.id)))
                .map(tag => [String(tag.id), tag]));
            const viewAuthority = new Map(requestedIds.map(id => {
                const subtype = String(storedById.get(id)?.type || '').toLowerCase();
                const permissionKey = subtype === 'scaffold' ? permissions.Tags.Scaffold.View
                    : subtype === 'permit' ? permissions.Tags.Permit.View
                        : subtype === 'container' ? permissions.Tags.Container.View : null;
                return [id, !!permissionKey && $has(permissionKey)];
            }));
            await HttpService.post({
                url: 'api/scaffolds/deleteScaffolds',
                data: { guids: requestedIds, assetId: scope.facilityId },
            });
            const geometryAuthorities = captureTagGeometryAuthorities(requestedIds, scope.facilityId);
            try {
                const { data } = await HttpService.get({
                    url: 'api/scaffolds/getAllAssetScaffolds',
                    ...FACILITY_REQUEST,
                    params: { assetId: scope.facilityId },
                });
                const catalog = hydrateCatalog(data, ForgeScaffold, 'Scaffold');
                if (!isMutationScopeCurrent(scope)) return deleteReceipts(requestedIds, scope.facilityId);
                const retainedWithoutView = requestedIds
                    .filter(id => !viewAuthority.get(id) && storedById.has(id))
                    .map(id => storedById.get(id));
                commit('setScaffolds', [...catalog, ...retainedWithoutView]);
                recordCatalogTagGeometry(catalog, scope, geometryAuthorities);
                const authoritative = new Map(deleteReceipts(
                    requestedIds.filter(id => viewAuthority.get(id)),
                    scope.facilityId,
                    catalog,
                    true,
                ).map(receipt => [receipt.tagId, receipt]));
                return requestedIds.map(id => authoritative.get(id)
                    || deleteReceipts([id], scope.facilityId)[0]);
            } catch (err) {
                debugError('❌ Scaffold delete refresh failed:', err);
                return deleteReceipts(requestedIds, scope.facilityId);
            }
        },
        async DELETE_ANNOTATION_TAGS({ commit }, { ids }) {
            const requestedIds = uniqueTagIds(ids);
            const scope = captureMutationScope();
            const headers = { 'X-Facility-Id': scope.facilityId };
            await HttpService.post({
                url: 'api/annotations/deleteAnnotations',
                headers,
                data: { guids: requestedIds, assetId: scope.facilityId },
            });
            const geometryAuthorities = captureTagGeometryAuthorities(requestedIds, scope.facilityId);
            try {
                const { data } = await HttpService.get({
                    url: 'api/annotations/getAllAssetAnnotation',
                    ...FACILITY_REQUEST,
                    headers,
                    params: { assetId: scope.facilityId, userId: scope.principalId },
                });
                const catalog = hydrateCatalog(data, ForgeAnnotation, 'Annotation');
                if (!isMutationScopeCurrent(scope)) return deleteReceipts(requestedIds, scope.facilityId);
                commit('setAnnotations', catalog);
                recordCatalogTagGeometry(catalog, scope, geometryAuthorities);
                return deleteReceipts(requestedIds, scope.facilityId, catalog, true);
            } catch (err) {
                debugError('❌ Annotation delete refresh failed:', err);
                return deleteReceipts(requestedIds, scope.facilityId);
            }
        },
        async DELETE_INSPECTION_TAGS({ dispatch, commit }, { ids, fetched = false }) {
            const requestedIds = uniqueTagIds(ids);
            const scope = captureMutationScope();
            await HttpService.post({
                url: fetched ? 'api/AMIS/delete' : 'api/forge/inspections/deleteInspections',
                data: { assetId: scope.facilityId, guids: requestedIds },
            });
            const geometryAuthorities = captureTagGeometryAuthorities(requestedIds, scope.facilityId);
            try {
                const catalog = await dispatch('fetchAllAMISTagsDbids', {
                    assetId: scope.facilityId,
                    fetched,
                    force: true,
                    redraw: false,
                    commitResult: false,
                });
                if (!isMutationScopeCurrent(scope)) return deleteReceipts(requestedIds, scope.facilityId);
                commit('SET_ALL_EXISTING_AMIS_TAGS', catalog);
                recordCatalogTagGeometry(catalog, scope, geometryAuthorities);
                return deleteReceipts(requestedIds, scope.facilityId, catalog, true);
            } catch (err) {
                debugError('❌ Inspection delete refresh failed:', err);
                return deleteReceipts(requestedIds, scope.facilityId);
            }
        },
        PUSH_ANOMALY_TAG({ dispatch, commit, state }, { tag }) {
            commit('UPSERT_ANOMALY_TAG', tag);
        },
        CREATE_ANOMALY_TAG({ dispatch, commit, state, getters }, { tag, thumbnail, dbIds: selectedDbIds }) {
            const scope = captureMutationScope();
            return new Promise((resolve, reject) => {
                // Anomaly create never sends tag.children (the API rejects it), so the picked elements
                // arrive as their own payload field. Any other caller still derives them from children.
                let dbIds = selectedDbIds?.length ? selectedDbIds : (tag.children ?? []).map((e) => e.dbId);
                let dbIdChain = [];
                if (store.state.viewer) {
                    dbIdChain = dbIds.reduce((acc, dbId) => {
                        let chain = [dbId, ...getAllParentsIds(store.state.viewer, dbId)];
                        return acc.concat(chain);
                    }, []);
                }
                debugLog(`🔗 Create anomaly dbIdChain: ${dbIdChain.length} entries from ${dbIds.length} ${selectedDbIds?.length ? 'payload' : 'children'} dbIds`);

                HttpService.post({
                    url: 'api/tags/anomalies/createAnomaly',
                    data: {
                        "tag": tag,
                        "Img64": thumbnail,
                        "dbIdChain": dbIdChain,
                        "assetId": router.currentRoute.params.id
                    },
                }).then(async ({ data }) => {
                    tag = Object.assign(new ForgeAnomaly(), data);
                    tag.parseContent();
                    await reconcileCreatedTag({
                        tag, scope, commit, mutation: 'UPSERT_ANOMALY_TAG', dispatch,
                    });
                    resolve(tag);
                }).catch(error => {
                    debugError("❌ Create anomaly: ", error);
                    reject(error);
                });
            });
        },

        PUSH_PIPELINE_TAG({ dispatch, commit, state }, { tag }) {
            commit('UPSERT_PIPELINE_TAG', tag);

            state.updateComponentUsingTags = !state.updateComponentUsingTags;
        },
        CREATE_PIPELINE_TAG({ dispatch, commit, state, getters }, { tag, thumbnail }) {
            const scope = captureMutationScope();
            return new Promise((resolve, reject) => {
                let dbIds = (tag.children ?? []).map((e) => e.dbId);
                let dbIdChain = [];
                if (store.state.viewer) {
                    dbIdChain = dbIds.reduce((acc, dbId) => {
                        let chain = [dbId, ...getAllParentsIds(store.state.viewer, dbId)];
                        return acc.concat(chain);
                    }, []);
                }

                HttpService.post({
                    url: 'api/tags/pipeline/createPipeline',
                    data: {
                        "tag": tag,
                        "Img64": thumbnail,
                        "dbIdChain": dbIdChain,
                        "assetId": router.currentRoute.params.id
                    },
                }).then(async ({ data }) => {
                    tag = Object.assign(new ForgePipeline(), data);
                    tag.parseContent()
                    await reconcileCreatedTag({
                        tag, scope, commit, mutation: 'UPSERT_PIPELINE_TAG', dispatch,
                    });

                    resolve(tag);

                }).catch(error => {

                    debugError("❌ Create pipeline: ", error);

                    reject(error);
                });
            });
        },
        PUSH_STRUCTURAL_TAG({ dispatch, commit, state }, { tag }) {
            commit('UPSERT_STRUCTURAL_TAG', tag);

            state.updateComponentUsingTags = !state.updateComponentUsingTags;
        },
        CREATE_STRUCTURAL_TAG({ dispatch, commit, state, getters }, { tag, thumbnail }) {
            const scope = captureMutationScope();

            return new Promise((resolve, reject) => {
                let dbIds = (tag.children ?? []).map((e) => e.dbId);
                let dbIdChain = [];
                if (store.state.viewer) {
                    dbIdChain = dbIds.reduce((acc, dbId) => {
                        let chain = [dbId, ...getAllParentsIds(store.state.viewer, dbId)];
                        return acc.concat(chain);
                    }, []);
                }

                HttpService.post({
                    url: 'api/tags/structural/createStructural',
                    data: {
                        "tag": tag,
                        "Img64": thumbnail,
                        "dbIdChain": dbIdChain,
                        "assetId": router.currentRoute.params.id
                    },
                }).then(async ({ data }) => {
                    tag = Object.assign(new ForgeStructural(), data);
                    tag.parseContent()
                    await reconcileCreatedTag({
                        tag, scope, commit, mutation: 'UPSERT_STRUCTURAL_TAG', dispatch,
                    });
                    resolve(tag);
                }).catch(error => {

                    debugError("❌ Create Structural: ", error);

                    reject(error);
                });
            });
        },
        CREATE_AVA_INSPECT_TAG({ dispatch, commit, state, getters }, { tag, thumbnail, cropVolume, assetId }) {
            const scope = captureMutationScope(assetId || router.currentRoute.params.id);
            return new Promise((resolve, reject) => {
                let dbIds = (tag.children ?? []).map((e) => e.dbId);
                let dbIdChain = [];
                if (store.state.viewer) {
                    dbIdChain = dbIds.reduce((acc, dbId) => {
                        let chain = [dbId, ...getAllParentsIds(store.state.viewer, dbId)];
                        return acc.concat(chain);
                    }, []);
                }

                // Use provided assetId or fall back to route params
                const effectiveAssetId = assetId || router.currentRoute.params.id;

                HttpService.post({
                    url: 'api/tags/ava-inspect/createAvaInspect',
                    data: {
                        "tag": tag,
                        "Img64": thumbnail,
                        "dbIdChain": dbIdChain,
                        "assetId": effectiveAssetId,
                        "cropVolume": cropVolume || null
                    },
                }).then(async ({ data }) => {
                    tag = Object.assign(new ForgeAvaInspect(), data);
                    tag.parseContent();
                    await reconcileCreatedTag({
                        tag, scope, commit, mutation: 'UPSERT_AVA_INSPECT_TAG', dispatch,
                    });
                    resolve(tag);
                }).catch(error => {
                    debugError("❌ Create AVA Inspect: ", error);
                    reject(error);
                });
            });
        },
        UPDATE_AVA_INSPECT_TAG({ dispatch, commit, state, getters }, { tag, thumbnail, cropVolume, propagateUpdates = true, assetId }) {
            return new Promise((resolve, reject) => {
                // Use provided assetId, or tag's assetId, or fall back to route params
                const effectiveAssetId = assetId || tag?.assetId || router.currentRoute.params.id;

                HttpService.post({
                    url: `api/tags/ava-inspect/updateAvaInspectTag?propagateUpdates=${propagateUpdates}`,
                    data: {
                        "tag": tag,
                        "Img64": thumbnail,
                        "assetId": effectiveAssetId,
                        "cropVolume": cropVolume || null
                    },
                }).then(({ data }) => {
                    tag = Object.assign(new ForgeAvaInspect(), data);
                    tag.parseContent();
                    const index = state.avaInspectTags.findIndex(t => t.id === tag.id);
                    if (index !== -1) {
                        Vue.set(state.avaInspectTags, index, tag);
                    }
                    resolve(tag);
                }).catch(error => {
                    debugError("❌ Update AVA Inspect: ", error);
                    reject(error);
                });
            });
        },
        async FETCH_AVA_INSPECT_TAGS({ commit }, {
            assetId,
            force = false,
            redraw = true,
            commitResult = true,
        }) {
            const scope = captureMutationScope(assetId || router.currentRoute.params.id);
            const { data } = await HttpService.get({
                url: 'api/tags/ava-inspect/getAllTagsMinimalInfo',
                ...FACILITY_REQUEST,
                params: { assetId: scope.facilityId },
            });
            const tags = requireCatalogArray(data, 'AVA Inspect').map(row => {
                const tag = Object.assign(new ForgeAvaInspect(), row);
                tag.parseContent();
                return tag;
            });
            if (!isMutationScopeCurrent(scope)) throw new Error('Mutation scope changed during AVA Inspect refresh');
            if (commitResult) commit('SET_AVA_INSPECT_TAGS', tags);
            if (redraw) store.state.forgeService.update3dIconsInAllViews();
            debugLog(`⚡ All AVA Inspect tags fetched: ${tags.length}${force ? ' (fresh)' : ''}`);
            return tags;
        },
        /**
         * GET api/tags/ava-inspect/{id} resolves the facility from the X-Facility-Id HEADER and
         * nothing else (FacilityContextMiddleware), and answers a missing one as 400 "Tag IS Not
         * Found!" - the same body a genuinely absent tag gets. Without the header every AVA Inspect
         * 3D marker click failed with that message, on a tag that was present and in scope.
         */
        FETCH_AVA_INSPECT_TAG_BY_ID({ commit, state, rootState }, id) {
            return new Promise((resolve, reject) => {
                HttpService.get({
                    url: `api/tags/ava-inspect/${id}`,
                    headers: { 'X-Facility-Id': rootState.assetId },
                }).then(({ data }) => {
                    let tag = Object.assign(new ForgeAvaInspect(), data);
                    tag.parseContent();
                    resolve(tag);
                }).catch(error => {
                    debugError("❌ Fetch AVA Inspect tag by ID: ", error);
                    reject(error);
                });
            });
        },
        async DELETE_AVA_INSPECT_TAG({ dispatch, commit }, { ids }) {
            const requestedIds = uniqueTagIds(ids);
            const scope = captureMutationScope();
            await HttpService.post({
                url: 'api/tags/ava-inspect/deleteAvaInspect',
                data: { ids: requestedIds, assetId: scope.facilityId },
            });
            const geometryAuthorities = captureTagGeometryAuthorities(requestedIds, scope.facilityId);
            try {
                const [manualResponse, minimal] = await Promise.all([
                    HttpService.get({
                        url: 'api/tags/ava-inspect/getAllManualAvaInspect',
                        ...FACILITY_REQUEST,
                        params: { assetId: scope.facilityId },
                    }),
                    dispatch('FETCH_AVA_INSPECT_TAGS', {
                        assetId: scope.facilityId, force: true, redraw: false, commitResult: false,
                    }),
                ]);
                const manual = hydrateCatalog(manualResponse.data, ForgeAvaInspect, 'Manual AVA Inspect');
                if (!isMutationScopeCurrent(scope)) return deleteReceipts(requestedIds, scope.facilityId);
                const catalog = [...manual, ...minimal];
                commit('SET_AVA_INSPECT_TAGS', minimal);
                recordCatalogTagGeometry(catalog, scope, geometryAuthorities);
                return deleteReceipts(requestedIds, scope.facilityId, catalog, true);
            } catch (err) {
                debugError('❌ AVA Inspect delete refresh failed:', err);
                return deleteReceipts(requestedIds, scope.facilityId);
            }
        },
        MARK_EQUIPMENT_AS_ISOLATED({ dispatch, commit, state, getters }, { ids }) {
            return new Promise((resolve, reject) => {
                HttpService.post({
                    url: 'api/tags/equipment/markAsIsolated',
                    data: ids,
                }).then(({ data }) => {
                    resolve(data);
                }).catch(error => {
                    debugError("❌ Mark equipment as isolated: ", error);
                    reject(error);
                });
            });
        },
        GET_FORGE_TAGS(_, query) {
            return new Promise((resolve, reject) => {
                HttpService.get({
                    url: 'api/baseTags/typeHead',
                    params: query,
                }).then(({ data }) => {
                    resolve(data);
                }).catch(error => {
                    debugError("❌ Get forge tags: ", error);
                    reject(error);
                });
            });
        },
        GET_SIMPLE_SYNC_TAGS({ state }, { cancelToken } = {}) {
            return new Promise((resolve, reject) => {
                HttpService.get({
                    url: `api/baseTags/${router.currentRoute.params.id}/tagsToSync`,
                    cancelToken
                }).then(({ data }) => {
                    resolve(data);
                }).catch(error => {
                    if (!axios.isCancel(error)) {
                        debugError("❌ Get simple sync tags: ", error);
                        reject(error);
                    }
                });
            });
        },
        UPDATE_CURRENT_SYNC_VERSION(_, version) {
            return new Promise((resolve, reject) => {
                HttpService.put({
                    url: `api/assets/${router.currentRoute.params.id}/updateSyncVersion`,
                    data: {
                        version
                    }
                }).then(({ data }) => {
                    resolve(data);
                }).catch(error => {
                    debugError("❌ Update current sync version: ", error);
                    reject(error);
                });
            });
        },
        SYNC_OBJECTS_TO_VERSION({ state }, objects) {
            return new Promise((resolve, reject) => {
                HttpService.put({
                    url: `api/baseTags/${router.currentRoute.params.id}/sync`,
                    data: objects
                }).then(({ data }) => {
                    resolve();
                }).catch(error => {
                    debugError("❌ Get simple sync tags: ", error);
                    reject(error);
                });
            });
        },
        UPDATE_TAGS_GROUPING(_, groupingJson) {
            return new Promise((resolve, reject) => {
                HttpService.post({
                    url: `api/assets/${router.currentRoute.params.id}/updateTagsGrouping`,
                    headers: {
                        "Content-Type": "text/json"
                    },
                    data: JSON.stringify(groupingJson)
                }).then(({ data }) => {
                    store.state.forgeSettings.tagsGroupingJson = groupingJson;
                    resolve(data);
                }).catch(error => {
                    debugError("❌ Update tags grouping: ", error);
                    reject(error);
                });
            });
        },
        GET_EQUIPMENT_TREE_VIEW(_, query) {
            return new Promise((resolve, reject) => {
                HttpService.get({
                    url: `api/tags/equipment/tree/TypeHead?${objectToQueryParams(query)}`
                }).then(({ data }) => {
                    resolve(data);
                }).catch(error => {
                    debugError("❌ Get equipment tree view: ", error);
                    reject(error);
                });
            });
        },
        Load_MORE_EQUIPMENT_TREE_CHILDREN(_, query) {
            return new Promise((resolve, reject) => {
                HttpService.get({
                    url: `api/tags/equipment/tree/TypeHead/LeafPage?${objectToQueryParams(query)}`,
                }).then(({ data }) => {
                    data.items = data.items.map(a => {
                        a = Object.assign(new ForgeEquipment(), a);
                        a.parseContent();
                        return a;
                    })
                    resolve(data);
                }).catch(error => {
                    debugError("❌ Load more tags tree children: ", error);
                    reject(error);
                });
            });
        },
        GET_PIPELINE_TREE_VIEW(_, query) {
            return new Promise((resolve, reject) => {
                HttpService.get({
                    url: `api/tags/pipeline/tree/TypeHead?${objectToQueryParams(query)}`
                }).then(({ data }) => {
                    resolve(data);
                }).catch(error => {
                    debugError("❌ Get pipeline tree view: ", error);
                    reject(error);
                });
            });
        },
        Load_MORE_PIPELINE_TREE_CHILDREN(_, query) {
            return new Promise((resolve, reject) => {
                HttpService.get({
                    url: `api/tags/pipeline/tree/TypeHead/LeafPage?${objectToQueryParams(query)}`,
                }).then(({ data }) => {
                    data.items = data.items.map(a => {
                        a = Object.assign(new ForgePipeline(), a);
                        a.parseContent();
                        return a;
                    })
                    resolve(data);
                }).catch(error => {
                    debugError("❌ Load more pipeline tree children: ", error);
                    reject(error);
                });
            });
        },
        InspectionsOneStepBack({ dispatch, commit, state }) {
            let previousTags = store.getters['GET_PREVIOUS_TAGS'];
            let editElement = store.getters['GET_EDITING_AMIS_TAG'];
            let isReadOnly = store.getters['GET_IS_AMIS_TAG_READ_ONLY'];
            let backToGlobalSearch = store.getters['TAGS_TYPES/GET_BACK_TO_GLOBAL_SEARCH'];

            if (!previousTags.length && !editElement) {
                store.dispatch('closeLeftTab');
                return;
            }

            if (previousTags.length) {
                store.dispatch('POP_TAG_FROM_PREVIOUS_TAGS');
            } else if (editElement) {
                if (isReadOnly) {
                    store.dispatch('clearEditAMISTagTab');

                    if (backToGlobalSearch) {
                        store.dispatch('TAGS_TYPES/SET_BACK_TO_GLOBAL_SEARCH', false);
                        store.state.mainApp.openGlobalSearch();
                    }
                } else {
                    Vue.prototype.$dialog.confirm({
                        text: 'Do you want to discard changes?',
                        title: 'Save Changes',
                        actions: {
                            false: {
                                text: 'Yes',
                                color: 'error',
                                handle: () => {
                                    store.commit('SET_IS_AMIS_TAG_READ_ONLY', true);
                                },
                            },
                            true: {
                                text: 'No',
                                color: 'primary'
                            },
                        },
                    });
                }
            } else {
                store.dispatch('closeLeftTab');
            }
        },

        /**
         * Saves a picked position for an Equipment/Pipeline/Structural Master-family tag.
         * Called from the position assignment callback after the raycast succeeds.
         *
         * `image360Id` is OPTIONAL: a point picked on the 3D model has no scan behind it, so it
         * is sent as an explicit null rather than omitted or faked. The caller decides whether
         * the open scan describes the point (see the `source` leg marker on the callback).
         *
         * `img64` is OPTIONAL: the `data:image/jpeg|png;base64,...` thumbnail captured at pick time,
         * sent unchanged. Absent or null is left off the request, so the server keeps the existing
         * thumbnail. It is never logged - it can be several hundred KB.
         *
         * REJECTS on write failure. Callers retain the existing point and apply only the typed
         * authoritative refresh returned on success. The user-facing toast is raised here.
         * @param {Object} payload - { tagId: string, payload: { forgeAssetId, image360Id, x, y, z, img64? } }
         */
        async SAVE_TAG_SCAN_POSITION({ dispatch, commit }, { tagId, payload }) {
            const { img64 = null, ...positionPayload } = payload || {};
            const scope = captureMutationScope(payload?.forgeAssetId);
            const image360Id = positionPayload.image360Id ?? null;
            try {
                debugLog('[SetPosition] Saving position for tag', {
                    tagId, scanLess: !image360Id, hasThumbnail: !!img64, payload: positionPayload,
                });
                await HttpService.post({
                    url: `api/baseTags/${tagId}/scanPosition`,
                    data: { ...positionPayload, image360Id, ...(img64 ? { img64 } : {}) },
                });
                debugLog('[SetPosition] Save success', { tagId, scanLess: !image360Id });
                return refreshTagAfterWrite(
                    dispatch,
                    commit,
                    tagId,
                    payload.forgeAssetId,
                    'set-position',
                    'Position saved successfully',
                    scope,
                );
            } catch (err) {
                if (!isMutationScopeCurrent(scope)) return { status: 'scope-changed' };
                debugError('[SetPosition] Save FAILED:', err);
                Vue.prototype.$dialog.notify.error(tagMutationErrorMessage(
                    err,
                    'Failed to save this position.',
                ), {
                    position: 'top-right',
                    timeout: 5000,
                });
                // Surfaced for logging; the caller has kept the previous position untouched.
                throw err;
            }
        },

        /**
         * Clears the saved position for a Master-family tag in a single facility.
         * Backend DELETE nullifies Point + Image360Id on the TagFacilityOverride row
         * keyed by (ForgeBaseTagId, ForgeAssetId). Other facilities' positions preserved.
         * @param {Object} payload - { tagId: string, forgeAssetId: string }
         */
        async CLEAR_TAG_SCAN_POSITION({ dispatch, commit }, { tagId, forgeAssetId }) {
            const scope = captureMutationScope(forgeAssetId);
            try {
                debugLog('[ClearPosition] Clearing position for tag:', tagId, 'facility:', forgeAssetId);
                await HttpService.delete({
                    url: `api/baseTags/${tagId}/scanPosition`,
                    params: { forgeAssetId },
                });
                debugLog('[ClearPosition] Clear success');
                return refreshTagAfterWrite(
                    dispatch,
                    commit,
                    tagId,
                    forgeAssetId,
                    'clear-position',
                    'Position cleared successfully',
                    scope,
                );
            } catch (err) {
                if (!isMutationScopeCurrent(scope)) return { status: 'scope-changed' };
                debugError('[ClearPosition] Clear FAILED:', err);
                Vue.prototype.$dialog.notify.error(tagMutationErrorMessage(
                    err,
                    'Failed to clear this position.',
                ), {
                    position: 'top-right',
                    timeout: 5000,
                });
                return {
                    status: 'failed',
                    error: err,
                    receipt: mutationReceipt({
                        operation: 'clear-position', tagId, facilityId: forgeAssetId,
                        outcome: TAG_MARKER_OUTCOME.CONFIRMED_FAILURE,
                        scope,
                    }),
                };
            }
        },
        /**
         * Clears an anomaly's position in one facility. Captured elements are kept (D4).
         * The server answers with the anomaly resolved for `assetId`; a body without an id is
         * treated as an acknowledgement and the anomaly is refetched for the current facility.
         * Never rejects: callers apply `tag` only when `status === 'refreshed'`.
         * @param {Object} payload - { tagId: string, assetId: string }
         */
        async CLEAR_ANOMALY_POSITION({ dispatch, commit }, { tagId, assetId }) {
            const scope = captureMutationScope(assetId);
            let data;
            try {
                debugLog('[ClearPosition] Clearing anomaly position', { tagId, assetId });
                ({ data } = await HttpService.delete({
                    url: `api/tags/anomalies/${tagId}/position`,
                    params: { assetId },
                }));
            } catch (err) {
                if (!isMutationScopeCurrent(scope)) return { status: 'scope-changed' };
                debugError('[ClearPosition] Anomaly clear FAILED:', err);
                Vue.prototype.$dialog.notify.error(tagMutationErrorMessage(
                    err,
                    'Failed to clear this position.',
                ), {
                    position: 'top-right',
                    timeout: 5000,
                });
                return {
                    status: 'failed',
                    error: err,
                    receipt: mutationReceipt({
                        operation: 'clear-position', tagId, facilityId: assetId,
                        outcome: TAG_MARKER_OUTCOME.CONFIRMED_FAILURE,
                        scope,
                    }),
                };
            }

            if (!isMutationScopeCurrent(scope)) return { status: 'scope-changed' };

            try {
                let tag;
                if (data?.id) {
                    tag = Object.assign(new ForgeAnomaly(), data);
                    tag.parseContent();
                } else {
                    tag = await dispatch('GET_BASE_TAG_BY_ID_FRESH', {
                        id: tagId, assetId, expectedKind: null,
                    });
                }
                Vue.prototype.$dialog.notify.success('Position cleared successfully', {
                    position: 'top-right',
                    timeout: 3000,
                });
                const receipt = mutationReceipt({
                    operation: 'clear-position', tagId, facilityId: assetId,
                    outcome: TAG_MARKER_OUTCOME.CONFIRMED_SUCCESS, tag,
                    scope,
                });
                commit?.('APPLY_AUTHORITATIVE_TAG_REFRESH', tag, { root: true });
                await dispatch('RECONCILE_TAG_MUTATION_RECEIPTS', [receipt]);
                return {
                    status: 'refreshed',
                    tag,
                    receipt,
                };
            } catch (err) {
                if (!isMutationScopeCurrent(scope)) return { status: 'scope-changed' };
                debugError('[ClearPosition] Anomaly cleared but the refresh failed', err);
                Vue.prototype.$dialog.notify.warning(writeSucceededButRefreshFailed, {
                    position: 'top-right',
                    timeout: 7000,
                });
                const receipt = mutationReceipt({
                    operation: 'clear-position', tagId, facilityId: assetId,
                    outcome: TAG_MARKER_OUTCOME.WRITE_SUCCEEDED_REFRESH_FAILED,
                    authoritativePoint: null,
                    scope,
                });
                await dispatch('RECONCILE_TAG_MUTATION_RECEIPTS', [receipt]);
                return {
                    status: TAG_MARKER_OUTCOME.WRITE_SUCCEEDED_REFRESH_FAILED,
                    error: err,
                    receipt,
                };
            }
        },
        /**
         * Saves a picked position for a General / Annotation tag in one facility.
         *
         * The narrow channel, and the only one: `editAnnotation` copies the model it is given field
         * by field and takes ForgeAssetId straight off the body, so placing through it rewrote
         * facility ownership and full-replaced children. This route reads nothing but
         * {assetId, point, image360Id, img64} and answers with the tag resolved for `assetId`.
         *
         * `image360Id` is null for a 3D pick - an explicit null, never omitted or faked. `img64` is
         * the pick-time thumbnail; absent leaves the stored thumbnail untouched. It is never
         * logged: it can be several hundred KB. The captured element does NOT ride along here - it
         * keeps its own append-only route, `POST api/baseTags/{tagId}/elements`.
         * @param {Object} payload - { tagId, assetId, point: {x,y,z}, image360Id?, img64? }
         */
        async SAVE_GENERAL_POSITION({ dispatch, commit }, { tagId, assetId, point, image360Id = null, img64 = null }) {
            debugLog('[SetPosition] Saving general tag position', {
                tagId, assetId, scanLess: !image360Id, hasThumbnail: !!img64, point,
            });
            return narrowPositionWrite({
                dispatch,
                commit,
                Model: ForgeAnnotation,
                tagId,
                assetId,
                operation: 'set-position',
                logTag: '[SetPosition] General',
                successMessage: 'Position saved successfully',
                failureMessage: 'Failed to save this position.',
                request: () => HttpService.post({
                    url: NARROW_POSITION_ROUTES.annotation(tagId),
                    data: {
                        assetId,
                        point,
                        image360Id: image360Id ?? null,
                        ...(img64 ? { img64 } : {}),
                    },
                }),
            });
        },
        /**
         * Clears a General / Annotation tag's position in one facility. Idempotent server-side, and
         * it never touches the linked 360 image info, the captured children or the thumbnail.
         * @param {Object} payload - { tagId: string, assetId: string }
         */
        async CLEAR_GENERAL_POSITION({ dispatch, commit }, { tagId, assetId }) {
            debugLog('[ClearPosition] Clearing general tag position', { tagId, assetId });
            return narrowPositionWrite({
                dispatch,
                commit,
                Model: ForgeAnnotation,
                tagId,
                assetId,
                operation: 'clear-position',
                logTag: '[ClearPosition] General',
                successMessage: 'Position cleared successfully',
                failureMessage: 'Failed to clear this position.',
                request: () => HttpService.delete({
                    url: NARROW_POSITION_ROUTES.annotation(tagId),
                    params: { assetId },
                }),
            });
        },
        /**
         * Saves a picked position for an Inspection tag in one facility. Same contract as
         * SAVE_GENERAL_POSITION above, against the inspection route and behind
         * `InspectionTags.Update`.
         * @param {Object} payload - { tagId, assetId, point: {x,y,z}, image360Id?, img64? }
         */
        async SAVE_INSPECTION_POSITION({ dispatch, commit }, { tagId, assetId, point, image360Id = null, img64 = null }) {
            debugLog('[SetPosition] Saving inspection position', {
                tagId, assetId, scanLess: !image360Id, hasThumbnail: !!img64, point,
            });
            return narrowPositionWrite({
                dispatch,
                commit,
                Model: ForgeInspection,
                tagId,
                assetId,
                operation: 'set-position',
                logTag: '[SetPosition] Inspection',
                successMessage: 'Position saved successfully',
                failureMessage: 'Failed to save this position.',
                request: () => HttpService.post({
                    url: NARROW_POSITION_ROUTES.inspection(tagId),
                    data: {
                        assetId,
                        point,
                        image360Id: image360Id ?? null,
                        ...(img64 ? { img64 } : {}),
                    },
                }),
            });
        },
        /**
         * Clears an Inspection tag's position in one facility. Idempotent server-side, and it never
         * touches the linked 360 image info, the captured children, the sub-tags or the thumbnail.
         * @param {Object} payload - { tagId: string, assetId: string }
         */
        async CLEAR_INSPECTION_POSITION({ dispatch, commit }, { tagId, assetId }) {
            debugLog('[ClearPosition] Clearing inspection position', { tagId, assetId });
            return narrowPositionWrite({
                dispatch,
                commit,
                Model: ForgeInspection,
                tagId,
                assetId,
                operation: 'clear-position',
                logTag: '[ClearPosition] Inspection',
                successMessage: 'Position cleared successfully',
                failureMessage: 'Failed to clear this position.',
                request: () => HttpService.delete({
                    url: NARROW_POSITION_ROUTES.inspection(tagId),
                    params: { assetId },
                }),
            });
        },
        /**
         * Saves a picked position for an AVA Inspect tag in one facility. Same contract as the two
         * actions above, against the AVA Inspect route and behind `Tags.AvaInspect.Update`.
         *
         * The narrow channel, and the only one: the old path posted the WHOLE tag through
         * `AVA_INSPECT/UPDATE_AVA_INSPECT_TAG`, whose service routes a body with no `.tag` to
         * `createAvaInspect` - which a ForgeAvaInspect never has, so every Set was POSTed as a
         * creation and refused. `ApplyMutableFields` on the full-save endpoint also copies
         * `linked360ImageInfo` straight from the body, so placing through it could wipe the
         * creation link. This route reads nothing but {assetId, point, image360Id, img64}, and the
         * server recomputes `heightFromDeck` from the new point inside the same write.
         * @param {Object} payload - { tagId, assetId, point: {x,y,z}, image360Id?, img64? }
         */
        async SAVE_AVA_INSPECT_POSITION({ dispatch, commit }, { tagId, assetId, point, image360Id = null, img64 = null }) {
            debugLog('[SetPosition] Saving AVA Inspect position', {
                tagId, assetId, scanLess: !image360Id, hasThumbnail: !!img64, point,
            });
            return narrowPositionWrite({
                dispatch,
                commit,
                Model: ForgeAvaInspect,
                tagId,
                assetId,
                operation: 'set-position',
                logTag: '[SetPosition] AVA Inspect',
                successMessage: 'Position saved successfully',
                failureMessage: 'Failed to save this position.',
                request: () => HttpService.post({
                    url: NARROW_POSITION_ROUTES.avaInspect(tagId),
                    data: {
                        assetId,
                        point,
                        image360Id: image360Id ?? null,
                        ...(img64 ? { img64 } : {}),
                    },
                }),
            });
        },
        /**
         * Clears an AVA Inspect tag's position in one facility. Idempotent server-side, and it
         * leaves the linked 360 image info, the captured children, the crop volume and the
         * thumbnail alone.
         *
         * `heightFromDeck` DOES go with the point - it is derived from it and nothing else, so the
         * server nulls it inside the same write (`ClearHeightFromDeckAsync`) and the resolved tag
         * carries that null back through TAG_PLACEMENT_FIELDS. The manually typed
         * `deckBelowElevation` is not a product of the placement and is left untouched.
         * @param {Object} payload - { tagId: string, assetId: string }
         */
        async CLEAR_AVA_INSPECT_POSITION({ dispatch, commit }, { tagId, assetId }) {
            debugLog('[ClearPosition] Clearing AVA Inspect position', { tagId, assetId });
            return narrowPositionWrite({
                dispatch,
                commit,
                Model: ForgeAvaInspect,
                tagId,
                assetId,
                operation: 'clear-position',
                logTag: '[ClearPosition] AVA Inspect',
                successMessage: 'Position cleared successfully',
                failureMessage: 'Failed to clear this position.',
                request: () => HttpService.delete({
                    url: NARROW_POSITION_ROUTES.avaInspect(tagId),
                    params: { assetId },
                }),
            });
        },
        async UNHARVEST_TAG({ dispatch, commit }, { tagId, forgeAssetId }) {
            const scope = captureMutationScope(forgeAssetId);
            let ack;
            try {
                debugLog('[Unharvest] Removing current-facility harvesting', { tagId, forgeAssetId });
                const response = await HttpService.post({
                    url: `api/baseTags/${tagId}/unharvest`,
                    data: { forgeAssetId },
                });
                ack = response.data;
            } catch (err) {
                if (!isMutationScopeCurrent(scope)) return { status: 'scope-changed' };
                const message = tagMutationErrorMessage(
                    err,
                    'Unable to unharvest this tag in the current facility.',
                );
                debugError('[Unharvest] Write failed', err);
                Vue.prototype.$dialog.notify.error(message, {
                    position: 'top-right',
                    timeout: 7000,
                });
                return {
                    status: 'failed',
                    error: err,
                    receipt: mutationReceipt({
                        operation: 'unharvest', tagId, facilityId: forgeAssetId,
                        outcome: TAG_MARKER_OUTCOME.CONFIRMED_FAILURE,
                        scope,
                    }),
                };
            }

            const refreshed = await refreshTagAfterWrite(
                dispatch,
                commit,
                tagId,
                forgeAssetId,
                'unharvest',
                'Tag unharvested in this facility',
                scope,
            );
            return { ...refreshed, ack };
        },
        /**
         * Every register row linked to one tag, cached per tenant/actor/view (AVA-493 Records tab).
         *
         * Never rejects: a failed fetch is committed as an `error` entry and returned like any
         * other, so the fire-and-forget dispatch a panel makes on mount cannot raise an unhandled
         * rejection. Read the result back through `GET_TAG_LINKED_RECORDS`.
         *
         * @param {Object} payload
         * @param {string} payload.tagId - The tag whose linked records to load
         * @param {boolean} [payload.force] - Fetch even when the tag is cached (the retry path)
         * @returns {Promise<Object|null>} { status, payload, error }, or null when no tagId was given
         */
        FETCH_TAG_LINKED_RECORDS({ commit, state, rootGetters = {} }, { tagId, force = false, viewContext, facilityId } = {}) {
            if (!tagId) return Promise.resolve(null);
            const selection = { viewContext, facilityId };
            const currentCacheKey = () => linkedRecordsCacheKey(tagId, selection,
                rootGetters['ACCOUNT/GET_COMPANY']?.id, rootGetters['ACCOUNT/GET_USER']?.id);
            const cacheKey = currentCacheKey();

            const cached = state.linkedRecordsByTagId[cacheKey];
            if (!force && cached?.status === TAG_LINKED_RECORDS_STATUS.LOADED) {
                // Touch: the cap evicts by write order, so a panel that only ever reads its cached
                // payload would age out from under itself once five other tags had been opened.
                commit('SET_TAG_LINKED_RECORDS_ENTRY', { tagId, cacheKey, entry: cached });
                return Promise.resolve(cached);
            }

            // Note an error entry does NOT short-circuit above: retry has to reach the server.
            const inFlight = linkedRecordsRequests.get(cacheKey);
            if (inFlight && !force) {
                // An explicit revisit may restore an evicted loading key without another HTTP read.
                commit('SET_TAG_LINKED_RECORDS_ENTRY', {
                    tagId,
                    cacheKey,
                    entry: { status: TAG_LINKED_RECORDS_STATUS.LOADING, payload: null, error: null },
                });
                return inFlight;
            }

            commit('SET_TAG_LINKED_RECORDS_ENTRY', {
                tagId,
                cacheKey,
                entry: { status: TAG_LINKED_RECORDS_STATUS.LOADING, payload: null, error: null },
            });

            const request = (async () => {
                try {
                    const payload = await registerService.getTagLinkedRegisterRows(tagId, selection);

                    // A payload without the registers array is not an empty result — it is a
                    // response we do not understand, and rendering the empty state for it would
                    // make a false claim about the tag.
                    if (!payload || !Array.isArray(payload.registers)) {
                        throw new Error('The server returned an unexpected response.');
                    }

                    const entry = {
                        status: TAG_LINKED_RECORDS_STATUS.LOADED,
                        payload: normalizeLinkedRecordsPayload(payload),
                        error: null,
                    };
                    // Latest-wins guard: a force refetch replaces this request in the map, and a
                    // superseded response must not overwrite the newer one's commit.
                    if (linkedRecordsRequests.get(cacheKey) !== request
                        || currentCacheKey() !== cacheKey
                        || !state.linkedRecordsByTagId[cacheKey]) return entry;
                    commit('SET_TAG_LINKED_RECORDS_ENTRY', { tagId, cacheKey, entry });
                    debugLog('[TagRecords] Linked records loaded', {
                        tagId,
                        registerCount: entry.payload.registers.length,
                        lockedCount: entry.payload.lockedRegisters.length,
                        totalRows: entry.payload.totalRows,
                        truncated: entry.payload.truncated,
                    });
                    return entry;
                } catch (err) {
                    const entry = {
                        status: TAG_LINKED_RECORDS_STATUS.ERROR,
                        payload: null,
                        error: linkedRecordsErrorMessage(err),
                    };
                    if (linkedRecordsRequests.get(cacheKey) !== request
                        || currentCacheKey() !== cacheKey
                        || !state.linkedRecordsByTagId[cacheKey]) return entry;
                    commit('SET_TAG_LINKED_RECORDS_ENTRY', { tagId, cacheKey, entry });
                    debugError('[TagRecords] Linked records fetch failed', { tagId, message: entry.error });
                    return entry;
                } finally {
                    // Clearing on the failure path too is what lets retry reach the server at all:
                    // a settled promise left behind would be handed to every later caller.
                    if (linkedRecordsRequests.get(cacheKey) === request) linkedRecordsRequests.delete(cacheKey);
                }
            })();

            linkedRecordsRequests.set(cacheKey, request);
            return request;
        },
    },

    getters: {
        GET_CUSTOM_FILTERS(state) {
            return state.customFilters;
        },
        GET_TAGS_SEARCH_TEXT(state) {
            return state.tagsSearchText;
        },
        GET_EDITING_TAG(state) {
            return state.editingTag;
        },
        GET_ADDING_TAG_INFO(state) {
            return state.addingTagInfo;
        },
        GET_ALL_EXISTING_AUTOMATIC_TAGS(state) {
            return state.allExistingAutomaticDbids
        },
        GET_EDITING_AMIS_TAG(state) {
            return state.editingAMISTag;
        },
        GET_EDITING_AVA_INSPECT_TAG(state) {
            return state.editingAvaInspectTag;
        },
        GET_AMIS_TAGS(state) {
            return state.amisTags;
        },
        GET_IS_TAG_READ_ONLY(state) {
            return state.isTagReadOnly;
        },
        GET_ACTIVE_DOCUMENT(state) {
            return state.activeDocument;
        },
        GET_IS_AMIS_TAG_READ_ONLY(state) {
            return state.isAMISTagReadOnly;
        },
        GET_IS_AVA_INSPECT_TAG_READ_ONLY(state) {
            return state.isAvaInspectTagReadOnly;
        },
        GET_PREVIOUS_TAGS(state) {
            return state.previousTags;
        },
        GET_IGNORED_TAGS_IDS(state) {
            return state.ignoredTagsIds;
        },
        GET_MUTABLE_IGNORED_TAGS_IDS(state) {
            return state.mutableIgnoredTagsIds;
        },
        GET_TAG_HARVESTER_INFO(state) {
            return state.tagHarvesterInfo;
        },
        GET_ANOMALIES_COUNT(state) {
            return state.anomaliesCount;
        },
        GET_FILTERED_ANOMALY_TAGS_IDS(state) {
            return state.filteredAnomalyTagsIds;
        },
        GET_ALL_EXISTING_ANOMALIES_TAGS(state) {
            return state.allExistingAnomaliesDbIds;
        },
        GET_MANUAL_STRUCTURAL_TAGS(state) {
            return state.manualStructuralTags;
        },
        GET_STRUCTURAL_TAGS(state) {
            return state.structuralTags;
        },
        GET_ALL_EXISTING_STRUCTURAL_TAGS(state) {
            return state.allExistingStructuralDbIds;
        },
        GET_STRUCTURAL_TAGS_COUNT(state) {
            return state.structuralTagsCount;
        },
        GET_FILTERED_STRUCTURAL_TAGS_IDS(state) {
            return state.filteredStructuralTagsIds;
        },
        GET_MANUAL_PIPELINES_TAGS(state) {
            return state.manualPipelinesTags;
        },
        GET_PIPELINES_TAGS(state) {
            return state.pipelinesTags;
        },
        GET_ALL_EXISTING_PIPELINES_TAGS(state) {
            return state.allExistingPipelineDbIds;
        },
        GET_PIPELINES_TAGS_COUNT(state) {
            return state.pipelinesTagsCount;
        },
        GET_FILTERED_PIPELINES_TAGS_IDS(state) {
            return state.filteredPipelineTagsIds;
        },
        GET_AVA_INSPECT_TAGS(state) {
            return state.avaInspectTags;
        },
        GET_AVA_INSPECT_TAGS_COUNT(state) {
            return state.avaInspectTagsCount;
        },
        GET_FILTERED_AVA_INSPECT_TAGS_IDS(state) {
            return state.filteredAvaInspectTagsIds;
        },
        GET_AUTOMATIC_TAGS(state) {
            return state.automaticTags;
        },
        GET_EXISTING_AUTOMATIC_TAGS_INFO(state) {
            return state.allExistingAutomaticDbids;
        },
        GET_AUTOMATIC_TAGS_COUNT(state) {
            return state.allAutmaticTagsCount;
        },
        GET_AMIS_TAGS_TOTAL_COUNT(state) {
            return state.allAMISTagsCount;
        },
        GET_AMIS_TAGS_SEARCH_COUNT(state) {
            return state.allAMISTagsSearchCount;
        },
        GET_ALL_AMIS_TAGS_IDS(state) {
            return state.allExistingAMISDbids ?? [];
        },
        GET_SELECTED_AUTOMATIC_TAGS_GROUP(state) {
            return state.selectedAutomaticTagsGroup;
        },
        GET_FILLING_AUTOMATIC_TAGS_IMAGES(state) {
            return state.fillingAutomaticTagsImages;
        },
        GET_FILLING_HARVESTER_TAGS_THUMBNAILS(state) {
            return state.fillingHarvesterTagsThumbnails;
        },
        GET_HARVESTER_TAGS_TO_CAPTURE(state) {
            return state.harvesterTagsToCapture;
        },
        GET_FILLING_AMIS_TAGS_IMAGES(state) {
            return state.fillingAMISTagsImages;
        },
        GET_FILLING_INSPECTIONS_IMAGES(state) {
            return state.fillingInspectionsImages;
        },
        GET_INSPECTIONS_TO_CAPTURE(state) {
            return state.inspectionDbIdsToCapture;
        },
        GET_LINKED_TAGS_CALLBACK(state) {
            return state.linkedTagsPopupCallback;
        },
        GET_FILTERED_AUTOMATIC_TAGS_IDS(state) {
            return state.filteredAutomaticTagsIds;
        },
        GET_AMIS_UNITS(state) {
            return state.amisUnits
        },
        GET_INSPECTION_SELECTED_UNITS(state) {
            return state.inspectionSelectedUnits;
        },
        // NEW GETTERS - Zones for spatial filtering
        GET_AVA_INSPECT_ZONES(state) {
            return state.avaInspectZones;
        },
        GET_SELECTED_ZONES(state) {
            return state.avaInspectZones.filter(z => z.isChecked);
        },
        GET_REQUESTED_IMAGES_IDS(state) {
            return state.requestedImagesId;
        },
        GET_AMIS_TOKEN(state) {
            return state.amisToken;
        },
        GET_ALL_AMIS_FILTERED_INSPECTIONS(state) {
            return state.allAMISFilteredInspections;
        },
        GET_FILTERED_INSPECTIONS(state) {
            return state.filteredInspection;
        },
        GET_SHOW_POPULATE_AUTOMATIC_TAGS_POPUP(state) {
            return state.showPopulateAutomaticTagsPopup;
        },
        GET_POPULATE_AUTOMATIC_TAGS_GROUP(state) {
            return state.populateAutomaticTagsGroup;
        },
        GET_THEMED_DB_IDS(state) {
            return state.themedDbIds;
        },
        GET_TAGS_ACTIVITIES(state) {
            return state.tagsActivities;
        },
        GET_AMIS_SEARCH_TEXT(state) {
            return state.amisSearchText;
        },
        GET_INSPECTION_IDS_FILTER(state) {
            const viewerMode = store.getters['VIEWERS_MODE/OPTIONS'];
            if (viewerMode?.action === 'show-tags-in-inspection-tab')
                return state.inspectionIdsFilter;
            if (viewerMode?.action === REGISTER_VIEWER_ACTION
                && viewerMode.metadata?.hasFilter === true
                && normalizeRegisterType(viewerMode.metadata?.registerType) === RegisterType.Inspection) {
                const tagIds = viewerMode.metadata?.tagIds || [];
                return tagIds.length ? tagIds : [REGISTER_FILTER_EMPTY_TAG_ID];
            }
            return [];
        },
        GET_LAST_AMIS_ADVANCED_FETCH_DATE(state) {
            return state.lastAMISAdvancedFetchDate;
        },
        GET_TAG_DYNAMIC_GRAPH(state) {
            return state.tagDynamicGraphs;
        },
        GET_DOCUMENTS_PANEL_CALLBACK(state) {
            return state.documentsPanelCallback;
        },
        GET_FILTERED_TAGS(state) {
            return state.filteredTags;
        },
        GET_INSPECTION_TAG_TREE(state) {
            return state.inspectionTagTree;
        },
        GET_SELECTED_INSPECTION_TREE_TAG(state) {
            return state.selectedInspectionTreeTag;
        },
        GET_SELECTED_INSPECTION_PROPERTY_FILTER_INDEX(state) {
            // Prefer the dedicated InspectionsSelectedFilter when present
            let sel = state.InspectionsSelectedFilter || state.InspectionSelectedFilter || 'No Filter';
            // Normalize for Last/Next Inspection which require type suffix in the options list
            if (sel === 'Last Inspection') {
                sel = `Last Inspection (${state.inspectionType || 'Thickness'})`;
            } else if (sel === 'Next Inspection') {
                sel = `Next Inspection (${state.inspectionType || 'Thickness'})`;
            }
            return state.InspectionFiltersOptions.indexOf(sel);
        },
        // NEW - Ava inspections selected filter index accessor (number)
        // Maps to AVA filter list order used by backend
        GET_SELECTED_AVA_INSPECTION_PROPERTY_FILTER_INDEX(state) {
            const avaFilterOrder = [
                'No Filter',
                'Coating Condition',
                'Corrosion Condition',
                'Severity'
            ];
            return avaFilterOrder.indexOf(state.AvaInspectionsSelectedFilter);
        },
        GET_IS_PROPERTY_ALLOW_NULL(state) {
            return !!store.state.TAGS.InspectionSelectedFilter?.includes('Inspection');
        },
        GET_SELECTED_COLORS_OPTIONS(state) {
            return state.selectedColorOptions;
        },
        GET_AVAILABLE_COLORS_OPTIONS(state) {
            return state.InspectionAvailableColorOptions;
        },
        GET_SHOW_TAG_MASTER_RESULT(state) {
            return state.showTagMasterResult;
        },
        GET_HARVESTED_NODES_IDS(state) {
            return state.harvestedNodesIds;
        },
        GET_HARVESTERS(state) {
            return state.harvesters;
        },
        GET_GET_BASE_TAGS_TIMEOUT(state) {
            return state.getBaseTagsTimeout;
        },
        GET_INSPECTION_MASTER_TYPE_OPTIONS(state) {
            return state.inspectionMasterTypeOptions;
        },
        GET_SELECTED_MASTER_TYPE_OPTIONS(state) {
            return state.inspectionMasterTypeOptions.filter(o => o.isChecked).map(o => o.name) ?? [];
        },
        GET_IS_EQUIPMENT_MASTER_AVAILABLE(state) {
            return state.isEquipmentMasterAvailable;
        },
        GET_IS_STRUCTURAL_MASTER_AVAILABLE(state) {
            return state.isStructuralMasterAvailable;
        },
        GET_IS_PIPELINE_MASTER_AVAILABLE(state) {
            return state.isPipelineMasterAvailable;
        },
        GET_IS_INSPECTION_TREE_OPTION(state) {
            return state.isInspectionTreeOption;
        },
        GET_ALL_EXISTING_INSPECTION_DB(state) {
            return state.allExistingAMISDbids;
        },
        IS_AI_INSPECTION_PROPERTY_SELECTED(state) {
            return state.InspectionSelectedFilter == 'AI Inspection';
        },
        GET_CML_MARKER_OWNER(state) {
            return state.cmlMarkerOwner;
        },
        // Keyed accessor for one tag's linked register records. Returns undefined until
        // FETCH_TAG_LINKED_RECORDS has run for that tag.
        GET_TAG_LINKED_RECORDS(state, getters, rootState, rootGetters = {}) {
            return (tagId, selection = {}) => (tagId ? state.linkedRecordsByTagId[linkedRecordsCacheKey(
                tagId, selection, rootGetters['ACCOUNT/GET_COMPANY']?.id, rootGetters['ACCOUNT/GET_USER']?.id,
            )] : undefined);
        },
    },
}
